import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  CalendarOff,
  Check,
  Copy,
  Crown,
  DoorOpen,
  Link2,
  Package,
  Pencil,
  Plus,
  Scissors,
  Search,
  ShoppingBag,
  Trash2,
  Users,
  X,
} from "lucide-react";
import {
  Badge,
  Button,
  ColorSwatchPicker,
  colorSwatchClass,
  DateTimePicker,
  Drawer,
  EmptyState,
  FieldHint,
  Input,
  Label,
  Modal,
  ModalFooter,
  Select,
  Skeleton,
  Textarea,
  useConfirm,
  useToast,
} from "@/components/ui";
import { api, ApiError } from "@/lib/api";
import { bookingGridMinutes, formatDateTime, formatMoney } from "@/lib/format";
import { makeUploadFilename, uploadFile } from "@/lib/upload";
import type {
  FormFrequency,
  FormTemplate,
  Product,
  Provider,
  ProviderTimeOff,
  Room,
  Service,
  ServiceCategory,
  PackageCategory,
  SpaPackage,
  PackageItem,
  WeeklySchedule,
  Fulfilment,
} from "@/lib/types";
import { cn } from "@/lib/utils";

/* ─── Local models ─────────────────────────────────────────────────────────── */

type CatalogService = Service;

interface MembershipBenefit {
  id?: string;
  service_id: string | null;
  /** Everything this line may be spent on; one entry for a single-treatment line. */
  service_ids?: string[];
  choice_names?: string[];
  /** A product included with the membership, instead of a treatment. */
  product_id?: string | null;
  product_name?: string | null;
  label?: string | null;
  position?: number;
  sessions_per_cycle: number;
  service_name?: string | null;
}

type Interval = "month" | "two_months" | "year";

/** "/month", "/2 months", "/year". */
function perCycle(interval: Interval): string {
  return interval === "year" ? "/year" : interval === "two_months" ? "/2 months" : "/month";
}

interface Membership {
  id: string;
  name: string;
  description: string | null;
  price_cents: number;
  interval: Interval;
  credit_cents: number;
  discount_pct: number;
  benefits: MembershipBenefit[];
  active_members: number;
}

/** `/pos/memberships` hands back raw rows — bigint/numeric columns arrive as strings. */
interface MembershipRow {
  id: string;
  name: string;
  description: string | null;
  price_cents: string | number;
  interval: string;
  credit_cents: string | number;
  discount_pct: string | number;
  benefits: {
    id?: string; service_id: string | null; service_ids?: string[]; choice_names?: string[];
    product_id?: string | null; product_name?: string | null;
    label?: string | null; position?: number;
    sessions_per_cycle: string | number; service_name?: string | null;
  }[] | null;
  active_members: string | number;
}

function toMembership(r: MembershipRow): Membership {
  return {
    id: r.id,
    name: r.name,
    description: r.description,
    price_cents: Number(r.price_cents) || 0,
    interval: r.interval === "year" ? "year" : r.interval === "two_months" ? "two_months" : "month",
    credit_cents: Number(r.credit_cents) || 0,
    discount_pct: Number(r.discount_pct) || 0,
    benefits: (r.benefits ?? []).map((b) => ({
      id: b.id,
      service_id: b.service_id,
      service_ids: b.service_ids ?? [],
      choice_names: b.choice_names ?? [],
      product_id: b.product_id ?? null,
      product_name: b.product_name ?? null,
      label: b.label ?? null,
      position: Number(b.position) || 0,
      sessions_per_cycle: Number(b.sessions_per_cycle) || 1,
      service_name: b.service_name ?? null,
    })),
    active_members: Number(r.active_members) || 0,
  };
}

/* ─── Shared helpers ───────────────────────────────────────────────────────── */

function errMsg(err: unknown): string {
  return err instanceof ApiError ? err.message : "Something went wrong";
}

/** "49.50" → 4950. Anything unparseable becomes 0 rather than NaN-poisoning the API. */
function dollarsToCents(v: string): number {
  const n = parseFloat(v);
  return Number.isFinite(n) ? Math.round(n * 100) : 0;
}

function centsToDollars(cents: number): string {
  return (cents / 100).toFixed(2);
}

const SECTION_HEADING = "text-[11px] font-semibold uppercase tracking-wider text-zinc-500";
const CARD = "rounded-xl border border-zinc-800/70 bg-zinc-900/30 p-5";
const CHECKBOX = "h-4 w-4 rounded border-zinc-700 bg-zinc-900 text-indigo-500 accent-indigo-500";

type DayKey = "mon" | "tue" | "wed" | "thu" | "fri" | "sat" | "sun";
const DAYS: { key: DayKey; label: string }[] = [
  { key: "mon", label: "Mon" },
  { key: "tue", label: "Tue" },
  { key: "wed", label: "Wed" },
  { key: "thu", label: "Thu" },
  { key: "fri", label: "Fri" },
  { key: "sat", label: "Sat" },
  { key: "sun", label: "Sun" },
];

const ROOM_KINDS: { value: string; label: string }[] = [
  // The first four are what her practice actually runs on; the rest stay for
  // any seat she adds later.
  { value: "drip_chair", label: "Drip chair" },
  { value: "consult_room", label: "Consult room" },
  { value: "virtual", label: "Virtual visit" },
  { value: "phone", label: "Phone visit" },
  { value: "pickup", label: "Medication pick-up" },
  { value: "mobile", label: "Mobile / concierge" },
  { value: "treatment_room", label: "Treatment room" },
  { value: "facial_room", label: "Facial room" },
  { value: "massage_room", label: "Massage room" },
  { value: "device", label: "Device/equipment" },
  { value: "other", label: "Other" },
];

function roomKindLabel(kind: string): string {
  return ROOM_KINDS.find((k) => k.value === kind)?.label ?? "Other";
}

/* ─── Page shell ───────────────────────────────────────────────────────────── */

type Tab = "services" | "rooms" | "providers" | "packages" | "memberships" | "products";
const TABS: { k: Tab; l: string; Icon: React.ComponentType<{ className?: string }> }[] = [
  { k: "services", l: "Services", Icon: Scissors },
  { k: "rooms", l: "Rooms", Icon: DoorOpen },
  { k: "providers", l: "Providers", Icon: Users },
  { k: "packages", l: "Packages", Icon: Package },
  { k: "memberships", l: "Memberships", Icon: Crown },
  { k: "products", l: "Products", Icon: ShoppingBag },
];

export function ServicesPage() {
  const navigate = useNavigate();
  const { tab: tabParam } = useParams<{ tab?: string }>();
  const tab: Tab = (TABS.some((t) => t.k === tabParam) ? tabParam : "services") as Tab;

  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-xl font-semibold text-zinc-100 sm:text-2xl">Services &amp; Rooms</h1>
          <p className="mt-1 text-sm text-zinc-500">
            Your treatment menu, rooms, and team — rooms are real resources here, never fake staff.
          </p>
        </div>
      </div>

      <BookingLinkCard />

      <div className="flex gap-1 border-b border-zinc-800">
        {TABS.map(({ k, l, Icon }) => (
          <button
            key={k}
            type="button"
            onClick={() => navigate(k === "services" ? "/services" : `/services/${k}`)}
            title={l}
            className={cn(
              "inline-flex flex-1 sm:flex-initial items-center justify-center gap-2 border-b-2 px-2 sm:px-3 py-2 text-sm font-medium transition-colors",
              tab === k ? "border-indigo-500 text-zinc-100" : "border-transparent text-zinc-400 hover:text-zinc-300"
            )}
          >
            <Icon className="h-4 w-4" />
            <span className="hidden sm:inline">{l}</span>
          </button>
        ))}
      </div>

      {tab === "services" && <ServicesTab />}
      {tab === "rooms" && <RoomsTab />}
      {tab === "providers" && <ProvidersTab />}
      {tab === "packages" && <PackagesTab />}
      {tab === "memberships" && <MembershipsTab />}
      {tab === "products" && <ProductsTab />}
    </div>
  );
}

/* ─── Online booking link card ─────────────────────────────────────────────── */

function BookingLinkCard() {
  const toast = useToast();
  const url = `${window.location.origin}/booking`;

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Booking link copied");
    } catch {
      toast.error("Couldn't copy — select the link and copy it manually");
    }
  };

  return (
    <div className="flex flex-col gap-2 rounded-xl border border-zinc-800/70 bg-zinc-900/30 px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
      <div className="min-w-0">
        <div className="flex items-center gap-2 text-sm text-zinc-200">
          <Link2 className="h-4 w-4 shrink-0 text-indigo-400" />
          <span className="truncate font-mono text-xs sm:text-sm">{url}</span>
        </div>
        <p className="mt-1 text-xs text-zinc-500">
          Clients book themselves here. New clients only see treatments marked &ldquo;New clients too&rdquo;; returning clients see everything with online booking on.
        </p>
      </div>
      <Button variant="secondary" size="sm" leftIcon={<Copy className="h-3.5 w-3.5" />} onClick={() => void copy()}>
        Copy
      </Button>
    </div>
  );
}

/* ═══ Services tab ═════════════════════════════════════════════════════════ */

function ServicesTab() {
  const toast = useToast();
  const confirm = useConfirm();
  const [services, setServices] = useState<CatalogService[]>([]);
  const [categories, setCategories] = useState<ServiceCategory[]>([]);
  const [providers, setProviders] = useState<Provider[]>([]);
  const [rooms, setRooms] = useState<Room[]>([]);
  const [forms, setForms] = useState<FormTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState<CatalogService | "new" | null>(null);
  const [editingCategory, setEditingCategory] = useState<ServiceCategory | "new" | null>(null);

  const load = useCallback(async () => {
    try {
      const [svc, cats, provs, rms, fms] = await Promise.all([
        api.get<{ services: CatalogService[] }>("/services"),
        api.get<{ categories: ServiceCategory[] }>("/services/categories"),
        api.get<{ providers: Provider[] }>("/providers"),
        api.get<{ rooms: Room[] }>("/rooms"),
        api.get<{ templates: FormTemplate[] }>("/forms"),
      ]);
      setServices(svc.services.filter((s) => !s.archived));
      setCategories(cats.categories.filter((c) => !c.archived));
      setProviders(provs.providers.filter((p) => p.active));
      setRooms(rms.rooms.filter((r) => r.active));
      setForms(fms.templates.filter((f) => !f.archived));
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => { void load(); }, [load]);

  /* 170 treatments is a long scroll to find one — same fix as the booking
     dialog: type a word and the menu narrows to it. */
  const [query, setQuery] = useState("");
  const groups = useMemo(() => {
    const q = query.trim().toLowerCase();
    const sorted = [...categories].sort((a, b) => a.position - b.position);
    const byCat = (id: string | null) =>
      services
        .filter((s) => s.category_id === id)
        .filter((s) => !q || s.name.toLowerCase().includes(q) || (s.category_name ?? "").toLowerCase().includes(q))
        .sort((a, b) => a.position - b.position || a.name.localeCompare(b.name));
    const out: { id: string | null; name: string; color: string; services: CatalogService[] }[] = sorted.map((c) => ({
      id: c.id, name: c.name, color: c.color, services: byCat(c.id),
    }));
    const uncategorized = byCat(null);
    if (uncategorized.length > 0) out.push({ id: null, name: "Uncategorized", color: "zinc", services: uncategorized });
    return out.filter((g) => g.services.length > 0);
  }, [services, categories, query]);

  /* "That's great drag handles are great" — she curates the order of each
     section by hand (all the Glutathione together, then the Vitamin C, the
     rest at the bottom) and until now only I could change it. Drag a row
     onto another and the whole section is renumbered from what she sees. */
  const [dragging, setDragging] = useState<string | null>(null);
  const reorder = async (groupServices: CatalogService[], fromId: string, toId: string) => {
    if (fromId === toId) return;
    const ids = groupServices.map((s) => s.id);
    const from = ids.indexOf(fromId), to = ids.indexOf(toId);
    if (from < 0 || to < 0) return;
    ids.splice(to, 0, ...ids.splice(from, 1));
    // Paint it immediately; the save follows.
    setServices((prev) => prev.map((s) => {
      const i = ids.indexOf(s.id);
      return i < 0 ? s : { ...s, position: i };
    }));
    try {
      await Promise.all(ids.map((id, i) => api.patch(`/services/${id}`, { position: i })));
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Couldn't save that order");
      void load();
    }
  };

  const archiveService = async (svc: CatalogService) => {
    const ok = await confirm({
      title: `Archive ${svc.name}?`,
      description: "It disappears from the menu and online booking. Existing appointments keep it.",
      confirmLabel: "Archive",
      danger: true,
    });
    if (!ok) return;
    try {
      await api.delete(`/services/${svc.id}`);
      toast.success("Service archived");
      void load();
    } catch (err) {
      toast.error(errMsg(err));
    }
  };

  if (loading) {
    return <div className="space-y-2">{Array.from({ length: 5 }).map((_, i) => <Skeleton key={i} className="h-16" />)}</div>;
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex flex-wrap items-center gap-1.5">
          {categories.map((c) => (
            <button
              key={c.id}
              type="button"
              onClick={() => setEditingCategory(c)}
              title="Edit category"
              className="inline-flex items-center gap-1.5 rounded-full border border-zinc-800 bg-zinc-900/50 px-2.5 py-1 text-xs text-zinc-300 hover:border-zinc-600 hover:text-zinc-100"
            >
              <span className={cn("h-2 w-2 rounded-full", colorSwatchClass(c.color))} />
              {c.name}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-2">
          <Input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search treatments…"
            aria-label="Search treatments"
            leftIcon={<Search className="h-3.5 w-3.5" />}
            className="h-8 w-52"
          />
          <Button variant="secondary" size="sm" leftIcon={<Plus className="h-3.5 w-3.5" />} onClick={() => setEditingCategory("new")}>
            New category
          </Button>
          <Button size="sm" leftIcon={<Plus className="h-3.5 w-3.5" />} onClick={() => setEditing("new")}>
            New service
          </Button>
        </div>
      </div>

      {groups.length === 0 && query.trim() ? (
        <p className="rounded-xl border border-dashed border-zinc-800 bg-zinc-900/30 px-4 py-6 text-center text-sm text-zinc-500">
          Nothing matches "{query}".
        </p>
      ) : groups.length === 0 ? (
        <EmptyState
          icon={Scissors}
          title="No services yet"
          description="Build your treatment menu — every service gets a duration, price, and the providers and rooms that can host it."
          action={<Button size="sm" leftIcon={<Plus className="h-3.5 w-3.5" />} onClick={() => setEditing("new")}>New service</Button>}
        />
      ) : (
        groups.map((g) => (
          <div key={g.id ?? "uncategorized"} className="space-y-2">
            <div className="flex items-center gap-2">
              <span className={cn("h-2.5 w-2.5 rounded-full", colorSwatchClass(g.color))} />
              <h2 className={SECTION_HEADING}>{g.name}</h2>
            </div>
            <div className="overflow-hidden rounded-xl border border-zinc-800/70 bg-zinc-900/30">
              {g.services.map((s, i) => (
                <div
                  key={s.id}
                  draggable
                  onDragStart={() => setDragging(s.id)}
                  onDragEnd={() => setDragging(null)}
                  onDragOver={(e) => { if (dragging && dragging !== s.id) e.preventDefault(); }}
                  onDrop={(e) => {
                    e.preventDefault();
                    if (dragging) void reorder(g.services, dragging, s.id);
                    setDragging(null);
                  }}
                  className={cn(dragging === s.id && "opacity-40")}
                >
                  <ServiceRow
                    service={s}
                    first={i === 0}
                    onEdit={() => setEditing(s)}
                    onArchive={() => void archiveService(s)}
                  />
                </div>
              ))}
            </div>
          </div>
        ))
      )}

      {editing !== null && (
        <ServiceDrawer
          service={editing}
          categories={categories}
          providers={providers}
          rooms={rooms}
          forms={forms}
          services={services}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); void load(); }}
        />
      )}
      {editingCategory !== null && (
        <CategoryModal
          category={editingCategory}
          onClose={() => setEditingCategory(null)}
          onSaved={() => { setEditingCategory(null); void load(); }}
        />
      )}
    </div>
  );
}

function ServiceRow({
  service: s, first, onEdit, onArchive,
}: {
  service: CatalogService;
  first: boolean;
  onEdit: () => void;
  onArchive: () => void;
}) {
  const counts: string[] = [];
  counts.push(`${s.provider_ids.length} ${s.provider_ids.length === 1 ? "provider" : "providers"}`);
  if (s.requires_room) {
    counts.push(s.room_ids.length === 0 ? "any room" : `${s.room_ids.length} ${s.room_ids.length === 1 ? "room" : "rooms"}`);
  }
  if (s.form_template_ids.length > 0) {
    counts.push(`${s.form_template_ids.length} ${s.form_template_ids.length === 1 ? "form" : "forms"}`);
  }
  if (s.addon_ids.length > 0) counts.push(`${s.addon_ids.length} add-ons`);

  return (
    <div className={cn("group flex items-center gap-3 px-4 py-3 hover:bg-zinc-900/40", !first && "border-t border-zinc-800/40")}>
      <span className={cn("h-2.5 w-2.5 shrink-0 rounded-full", colorSwatchClass(s.color))} />
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <span className="truncate text-sm font-medium text-zinc-100">{s.name}</span>
          {s.online_booking && <Badge color="emerald" size="xs">Online booking</Badge>}
          {s.online_booking && s.new_clients && <Badge color="blue" size="xs">New clients too</Badge>}
          {s.booking_message && <Badge color="violet" size="xs">Booking message</Badge>}
          {!s.requires_room && <Badge color="zinc" size="xs">No room needed</Badge>}
          {s.is_addon && <Badge color="emerald" size="xs">Add-on</Badge>}
        </div>
        <p className="mt-0.5 text-xs text-zinc-500">
          {s.duration_minutes} min
          {s.buffer_after_minutes > 0 && <span className="text-zinc-600"> +{s.buffer_after_minutes} min cleanup</span>}
          <span className="mx-1.5 text-zinc-700">·</span>
          {counts.join(" · ")}
        </p>
      </div>
      <span className="shrink-0 text-sm font-medium text-zinc-200 tabular-nums">{formatMoney(s.price_cents)}</span>
      <div className="flex shrink-0 items-center gap-1 opacity-0 transition-opacity group-hover:opacity-100 focus-within:opacity-100">
        <button
          type="button"
          onClick={onEdit}
          className="inline-flex h-7 w-7 items-center justify-center rounded text-zinc-400 hover:bg-zinc-800 hover:text-zinc-200"
          aria-label={`Edit ${s.name}`}
        >
          <Pencil className="h-3.5 w-3.5" />
        </button>
        <button
          type="button"
          onClick={onArchive}
          className="inline-flex h-7 w-7 items-center justify-center rounded text-zinc-400 hover:bg-zinc-800 hover:text-rose-300"
          aria-label={`Archive ${s.name}`}
        >
          <Trash2 className="h-3.5 w-3.5" />
        </button>
      </div>
    </div>
  );
}

/* ─── Category modal ───────────────────────────────────────────────────────── */

function CategoryModal({
  category, onClose, onSaved,
}: {
  category: ServiceCategory | "new";
  onClose: () => void;
  onSaved: () => void;
}) {
  const toast = useToast();
  const confirm = useConfirm();
  const isNew = category === "new";
  const base = isNew ? null : category;
  const [name, setName] = useState(base?.name ?? "");
  const [color, setColor] = useState(base?.color ?? "indigo");
  const [group, setGroup] = useState(base?.booking_group ?? "");
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!name.trim()) { toast.error("Give the category a name"); return; }
    setSaving(true);
    try {
      if (isNew) {
        await api.post("/services/categories", { name: name.trim(), color, booking_group: group.trim() || null });
        toast.success("Category created");
      } else {
        await api.patch(`/services/categories/${base!.id}`, { name: name.trim(), color, booking_group: group.trim() || null });
        toast.success("Category updated");
      }
      onSaved();
    } catch (err) {
      toast.error(errMsg(err));
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!base) return;
    const ok = await confirm({
      title: `Delete ${base.name}?`,
      description: "Services in this category are kept — they just become uncategorized.",
      confirmLabel: "Delete",
      danger: true,
    });
    if (!ok) return;
    try {
      await api.delete(`/services/categories/${base.id}`);
      toast.success("Category deleted");
      onSaved();
    } catch (err) {
      toast.error(errMsg(err));
    }
  };

  return (
    <Modal open onClose={onClose} title={isNew ? "New category" : "Edit category"} size="sm">
      <div className="space-y-4">
        <div className="flex items-end gap-2">
          <div className="flex-1">
            <Label htmlFor="cat-name" required>Name</Label>
            <Input
              id="cat-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Injectables"
              autoFocus
              onKeyDown={(e) => { if (e.key === "Enter") void save(); }}
            />
          </div>
          <ColorSwatchPicker value={color} onChange={setColor} />
        </div>
        {/* "When you seperate into the sections be sure to add the IV drips in
             the specialized area to show up with the drip section" */}
        <div>
          <Label htmlFor="cat-group">Shows on booking as</Label>
          <Input
            id="cat-group"
            value={group}
            onChange={(e) => setGroup(e.target.value)}
            placeholder={name.trim() || "Its own section"}
            onKeyDown={(e) => { if (e.key === "Enter") void save(); }}
          />
          <p className="mt-1 text-xs text-zinc-500">
            Leave this empty and the category is its own section on the booking page. Type the same
            thing here on two categories and clients see them as one section — this is how Specialized
            IV Drips shows with the drips.
          </p>
        </div>
        <ModalFooter>
          {!isNew && (
            <Button variant="ghost" className="mr-auto text-rose-300 hover:text-rose-200" onClick={() => void remove()}>
              Delete
            </Button>
          )}
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button loading={saving} onClick={() => void save()}>{isNew ? "Create" : "Save"}</Button>
        </ModalFooter>
      </div>
    </Modal>
  );
}

/* ─── Service drawer ───────────────────────────────────────────────────────── */

function CheckGroup({ title, hint, children }: { title: string; hint?: string; children: React.ReactNode }) {
  return (
    <div className="rounded-lg border border-zinc-800 p-3">
      <h3 className={cn(SECTION_HEADING, "mb-2")}>{title}</h3>
      <div className="space-y-1.5">{children}</div>
      {hint && <p className="mt-2 text-xs text-zinc-500">{hint}</p>}
    </div>
  );
}

/** A NEWLY ticked form starts at "First time only" unless it's the
    visit-update form, which by definition repeats. Existing links come back
    from the API with the frequency they were saved with. */
function defaultFormFrequency(form: FormTemplate | undefined): FormFrequency {
  return form?.slug === "visit-update" ? "every_visit" : "once";
}

function ServiceDrawer({
  service, categories, providers, rooms, forms, services, onClose, onSaved,
}: {
  service: CatalogService | "new";
  categories: ServiceCategory[];
  providers: Provider[];
  rooms: Room[];
  forms: FormTemplate[];
  /** The whole menu — the add-ons on it are what this treatment can offer. */
  services: CatalogService[];
  onClose: () => void;
  onSaved: () => void;
}) {
  const toast = useToast();
  const isNew = service === "new";
  const base = isNew ? null : service;

  const [name, setName] = useState(base?.name ?? "");
  const [categoryId, setCategoryId] = useState(base?.category_id ?? "");
  const [description, setDescription] = useState(base?.description ?? "");
  const [duration, setDuration] = useState(String(base?.duration_minutes ?? 60));
  const [buffer, setBuffer] = useState(String(base?.buffer_after_minutes ?? 0));
  const [price, setPrice] = useState(base ? centsToDollars(base.price_cents) : "");
  const [color, setColor] = useState(base?.color ?? "indigo");
  const [taxable, setTaxable] = useState(base?.taxable ?? false);
  const [onlineBooking, setOnlineBooking] = useState(base?.online_booking ?? true);
  const [requiresRoom, setRequiresRoom] = useState(base?.requires_room ?? true);
  const [newClients, setNewClients] = useState(base?.new_clients ?? false);
  /* Its mirror: "They can't book for the meds if they are returning they
     book the Return Consultation", and an Initial consultation is not for
     somebody who has been coming for months. */
  const [returningClients, setReturningClients] = useState(base?.returning_clients ?? true);
  const [bookingMessage, setBookingMessage] = useState(base?.booking_message ?? "");
  const [bookingNote, setBookingNote] = useState(base?.booking_note ?? "");
  const [formPacket, setFormPacket] = useState<string>(base?.form_packet ?? "");
  /**
   * The forms this treatment's PACKET sends by itself.
   *
   * "I did not see it with the forms" - her men's health consent was already
   * going out, because it lives in the Initial Testosterone folder and the
   * folder follows the packet. This list only ever showed the ones ticked by
   * hand, so the screen said three and the visit sent four.
   */
  const [packetForms, setPacketForms] = useState<{ id: string; name: string }[]>([]);
  useEffect(() => {
    if (!formPacket) { setPacketForms([]); return; }
    let gone = false;
    api.get<{ forms: { id: string; name: string }[] }>("/services/packet-forms", { packet: formPacket })
      .then((d) => { if (!gone) setPacketForms(d.forms ?? []); })
      .catch(() => { if (!gone) setPacketForms([]); });
    return () => { gone = true; };
  }, [formPacket]);
  const [creditsBack, setCreditsBack] = useState<boolean>(base?.credits_back === true);
  const [billsInsurance, setBillsInsurance] = useState<boolean>(base?.bills_insurance === true);
  /* "Patient comes in pays 25 (to us) dollars to verify insurance (Make a
     service for this) … To log name and payments received the 25 dollars" */
  const [isInsuranceCheck, setIsInsuranceCheck] = useState<boolean>(base?.is_insurance_check === true);
  /* "Only iv drips get the request for renewal forms". A treatment already on
     the menu keeps its own answer; a new one starts where the packet says, so
     a drip she adds next month asks for the renewal without her remembering. */
  const [sendsRenewal, setSendsRenewal] = useState<boolean>(base?.sends_renewal === true);
  const [renewalTouched, setRenewalTouched] = useState(false);
  /* A treatment being created follows its packet until she says otherwise, so
     a drip added next month asks for the renewal without her remembering. */
  useEffect(() => {
    if (base || renewalTouched) return;
    // The same three spellings of a push the server uses, plus the IM/SubQ
    // shots: "Butterfly IV Push …", "EH B12 IVP", "IVP Zofran 3MG".
    setSendsRenewal((formPacket === "iv" || formPacket === "nad")
      && !/push|ivp|intramuscular|subq|\bsq\b|\bim\b/i.test(name));
  }, [base, renewalTouched, formPacket, name]);
  const [billingCode, setBillingCode] = useState<string>(base?.billing_code ?? "");
  /**
   * The two that decide her consultation rules, and were reachable from
   * nowhere until now. Her Men's Health consultation sat with
   * "counts as the consultation" off, which quietly took testosterone out of
   * the new-client menu altogether.
   */
  const [countsAsConsult, setCountsAsConsult] = useState<boolean>(base?.counts_as_consult === true);
  /**
   * "I had two new consultation today so does the software put in the follow
   *  up consultation date 6 months from now?"
   *
   * It does, for a treatment ticked here. Seeded from her own names, so every
   * "… Consultation" on her menu already counts; the tick is how she corrects
   * the ones a name got wrong.
   */
  const [isConsultation, setIsConsultation] = useState<boolean>(base?.is_consultation === true);
  const [consultMonths, setConsultMonths] = useState<string>(
    base?.consult_required_months != null ? String(base.consult_required_months) : "");
  const [providerIds, setProviderIds] = useState<string[]>(base?.provider_ids ?? []);
  const [roomIds, setRoomIds] = useState<string[]>(base?.room_ids ?? []);
  const [formIds, setFormIds] = useState<string[]>(base?.form_template_ids ?? []);
  const [formFreq, setFormFreq] = useState<Record<string, FormFrequency>>(() =>
    Object.fromEntries((base?.form_rules ?? []).map((r) => [r.form_template_id, r.frequency])));
  const [isAddon, setIsAddon] = useState(base?.is_addon ?? false);
  const [addonIds, setAddonIds] = useState<string[]>(base?.addon_ids ?? []);
  const [saving, setSaving] = useState(false);

  /* Everything on the menu that IS an add-on, for the offer list below. */
  const addons = useMemo(
    () => services.filter((x) => x.is_addon && !x.archived && x.id !== base?.id),
    [services, base?.id],
  );

  const toggleId = (list: string[], set: (v: string[]) => void, id: string) => {
    set(list.includes(id) ? list.filter((x) => x !== id) : [...list, id]);
  };

  const freqFor = (formId: string): FormFrequency =>
    formFreq[formId] ?? defaultFormFrequency(forms.find((f) => f.id === formId));

  const save = async () => {
    if (!name.trim()) { toast.error("Give the service a name"); return; }
    const durationMin = parseInt(duration, 10);
    if (!Number.isFinite(durationMin) || durationMin <= 0) { toast.error("Duration must be at least 1 minute"); return; }
    const bufferMin = parseInt(buffer, 10);
    const body = {
      category_id: categoryId || null,
      name: name.trim(),
      description: description.trim() || null,
      duration_minutes: durationMin,
      buffer_after_minutes: Number.isFinite(bufferMin) && bufferMin > 0 ? bufferMin : 0,
      price_cents: dollarsToCents(price),
      taxable,
      color,
      online_booking: onlineBooking,
      requires_room: requiresRoom,
      new_clients: newClients,
      returning_clients: returningClients,
      booking_message: bookingMessage.trim() || null,
      booking_note: bookingNote.trim() || null,
      form_packet: formPacket || null,
      credits_back: creditsBack,
      bills_insurance: billsInsurance,
      is_insurance_check: isInsuranceCheck,
      sends_renewal: sendsRenewal,
      billing_code: billingCode.trim() || null,
      counts_as_consult: countsAsConsult,
      is_consultation: isConsultation,
      consult_required_months: consultMonths.trim() === "" ? null : Number(consultMonths),
      provider_ids: providerIds,
      room_ids: requiresRoom ? roomIds : [],
      form_template_ids: formIds,
      form_rules: formIds.map((id) => ({ form_template_id: id, frequency: freqFor(id) })),
      is_addon: isAddon,
      // An add-on doesn't carry add-ons of its own.
      addon_ids: isAddon ? [] : addonIds,
    };
    setSaving(true);
    try {
      if (isNew) {
        await api.post("/services", body);
        toast.success("Service created");
      } else {
        await api.patch(`/services/${base!.id}`, body);
        toast.success("Service updated");
      }
      onSaved();
    } catch (err) {
      toast.error(errMsg(err));
      setSaving(false);
    }
  };

  const formKindColor: Record<string, string> = { intake: "indigo", consent: "amber", custom: "zinc" };

  return (
    <Drawer open onClose={onClose} title={isNew ? "New service" : `Edit ${base?.name}`} width="lg">
      <div className="space-y-4 p-5">
        <div className="flex items-end gap-2">
          <div className="flex-1">
            <Label htmlFor="svc-name" required>Name</Label>
            <Input id="svc-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Signature Facial" autoFocus />
          </div>
          <ColorSwatchPicker value={color} onChange={setColor} />
        </div>

        <div>
          <Label htmlFor="svc-cat">Category</Label>
          <Select id="svc-cat" value={categoryId} onChange={(e) => setCategoryId(e.target.value)}>
            <option value="">No category</option>
            {categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </Select>
        </div>

        <div>
          <Label htmlFor="svc-desc">Description</Label>
          <Textarea
            id="svc-desc"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="What clients see when booking online."
            rows={3}
          />
        </div>

        <div className="grid grid-cols-3 gap-3">
          <div>
            <Label htmlFor="svc-duration" required>Duration (min)</Label>
            <Input id="svc-duration" type="number" min={5} step={5} value={duration} onChange={(e) => setDuration(e.target.value)} />
          </div>
          <div>
            <Label htmlFor="svc-buffer">Cleanup buffer (min)</Label>
            <Input id="svc-buffer" type="number" min={0} step={5} value={buffer} onChange={(e) => setBuffer(e.target.value)} />
            <FieldHint>Room stays blocked after.</FieldHint>
          </div>
          <div>
            <Label htmlFor="svc-price">Price ($)</Label>
            <Input
              id="svc-price"
              inputMode="decimal"
              value={price}
              onChange={(e) => setPrice(e.target.value)}
              placeholder="0.00"
            />
          </div>
        </div>

        <div>
          <div className="flex flex-wrap gap-x-5 gap-y-2">
            <label className="flex cursor-pointer items-center gap-2 text-sm text-zinc-300">
              <input type="checkbox" className={CHECKBOX} checked={taxable} onChange={(e) => setTaxable(e.target.checked)} />
              Taxable
            </label>
            <label className="flex cursor-pointer items-center gap-2 text-sm text-zinc-300">
              <input type="checkbox" className={CHECKBOX} checked={onlineBooking} onChange={(e) => setOnlineBooking(e.target.checked)} />
              Bookable online
            </label>
            <label className="flex cursor-pointer items-center gap-2 text-sm text-zinc-300">
              <input type="checkbox" className={CHECKBOX} checked={requiresRoom} onChange={(e) => setRequiresRoom(e.target.checked)} />
              Needs a room
            </label>
            <label className="flex cursor-pointer items-center gap-2 text-sm text-zinc-300">
              <input type="checkbox" className={CHECKBOX} checked={newClients} onChange={(e) => setNewClients(e.target.checked)} />
              New clients can book this online
            </label>
            <label className="flex cursor-pointer items-center gap-2 text-sm text-zinc-300">
              <input type="checkbox" className={CHECKBOX} checked={returningClients} onChange={(e) => setReturningClients(e.target.checked)} />
              Returning clients can book this online
            </label>
            <label className="flex cursor-pointer items-center gap-2 text-sm text-zinc-300">
              <input type="checkbox" className={CHECKBOX} checked={isAddon} onChange={(e) => setIsAddon(e.target.checked)} />
              This is an add-on
            </label>
          </div>
          <FieldHint>
            First-time clients only see treatments with this ticked (your IV drips and first consultations). Returning clients can book anything that's bookable online.
            {" "}An add-on goes into another treatment - it takes no chair and no extra time, just its price.
          </FieldHint>
        </div>

        {/* "can we have wording based on what they are booking" — this line
            sits under the treatment on the booking page, before they pick. */}
        <div>
          <Label htmlFor="svc-note">Note on the booking page</Label>
          <Textarea
            id="svc-note"
            value={bookingNote}
            onChange={(e) => setBookingNote(e.target.value)}
            placeholder="e.g. Your first IV consultation is done in the lounge or by video, and takes about 30 minutes."
            rows={2}
          />
          <FieldHint>
            Shown next to this treatment while a client is choosing, so they know what they're booking. Leave empty for nothing.
          </FieldHint>
        </div>

        {/* Which paperwork booking this sends: her packets. */}
        <div>
          <Label htmlFor="svc-packet">Paperwork this sends when booked</Label>
          <Select id="svc-packet" value={formPacket} onChange={(e) => setFormPacket(e.target.value)}>
            <option value="">Nothing</option>
            <option value="iv">IV / drips</option>
            <option value="nad">NAD+</option>
            <option value="weight_loss">Weight loss</option>
            <option value="peptides">Peptides</option>
            <option value="testosterone">Testosterone</option>
            <option value="injections">Wellness injections</option>
            <option value="cosmetic">Botox, Dysport and fillers</option>
          </Select>
          <FieldHint>
            A first-time client gets that Initial folder. A returning IV or NAD client gets the Renewal form.
            Returning weight loss, peptide, testosterone and wellness injection clients get nothing.
          </FieldHint>
        </div>

        {/* Whether this consultation is THE consultation for its treatments.
            Without it the whole path disappears for a first-timer. */}
        <label className="flex items-start gap-2 text-sm text-zinc-300">
          <input
            type="checkbox"
            checked={countsAsConsult}
            onChange={(e) => setCountsAsConsult(e.target.checked)}
            className="mt-0.5 h-4 w-4 rounded border-zinc-700 bg-zinc-900"
          />
          <span>
            This counts as the consultation
            <span className="mt-0.5 block text-[11px] text-zinc-500">
              Tick this on a consultation and it satisfies the requirement for everything in its
              paperwork folder. A first-timer choosing that path is offered this one. Without it,
              the path does not appear for new clients at all.
            </span>
          </span>
        </label>

        <label className="flex items-start gap-2 text-sm text-zinc-300">
          <input
            type="checkbox"
            checked={isConsultation}
            onChange={(e) => setIsConsultation(e.target.checked)}
            className="mt-0.5 h-4 w-4 rounded border-zinc-700 bg-zinc-900"
          />
          <span>
            This is a consultation
            <span className="mt-0.5 block text-[11px] text-zinc-500">
              When a visit with this treatment is marked done, the next consultation goes onto the
              client&rsquo;s chart six months out. Change the six in Manage &rarr; Settings.
            </span>
          </span>
        </label>

        <div>
          <Label htmlFor="svc-consult-months">A consultation lasts (months)</Label>
          <Input
            id="svc-consult-months"
            type="number"
            min={1}
            max={60}
            value={consultMonths}
            onChange={(e) => setConsultMonths(e.target.value)}
            placeholder="Leave blank if none is needed"
            className="w-56"
          />
          <p className="mt-1 text-[11px] text-zinc-500">
            On a TREATMENT, not the consultation: how long before they need another one. Yours is 6
            for IV. Booking online stops and offers the consultation instead when it has run out;
            the desk is never stopped.
          </p>
        </div>

        {/* "There is a $150 consultation fee, which will be charged at the time
            of your consultation. This $150 will then be credited in full
            toward your Heavenly Hydration service." */}
        {/* "have to figure out how to make the software understand if we are
            billing insurance or not billing insurance maybe a check off for
            the service????" */}
        <label className="flex items-start gap-2 text-sm text-zinc-300">
          <input
            type="checkbox"
            checked={billsInsurance}
            onChange={(e) => setBillsInsurance(e.target.checked)}
            className="mt-0.5 h-4 w-4 rounded border-zinc-700 bg-zinc-900"
          />
          <span>
            We bill insurance for this
            <span className="mt-0.5 block text-[11px] text-zinc-500">
              Out of network. Adding it to a client&rsquo;s insurance sheet brings this
              treatment&rsquo;s name and its usual code with it.
            </span>
          </span>
        </label>
        {billsInsurance && (
          <div>
            <Label>Billing code</Label>
            <Input
              value={billingCode}
              onChange={(e) => setBillingCode(e.target.value)}
              placeholder="99213"
            />
            <p className="mt-1 text-[11px] text-zinc-500">
              The code this usually goes out under. A visit can still be changed to another.
            </p>
          </div>
        )}

        <label className="flex items-start gap-2 text-sm text-zinc-300">
          <input
            type="checkbox"
            checked={sendsRenewal}
            onChange={(e) => { setSendsRenewal(e.target.checked); setRenewalTouched(true); }}
            className="mt-0.5 h-4 w-4 rounded border-zinc-700 bg-zinc-900"
          />
          <span>
            Ask returning clients for the renewal
            <span className="mt-0.5 block text-[11px] text-zinc-500">
              The short update before a drip. On for the IV and NAD bags, off for
              the pushes and everything else, so a two-minute push does not ask
              for the same paperwork as an hour-long bag.
            </span>
          </span>
        </label>

        <label className="flex items-start gap-2 text-sm text-zinc-300">
          <input
            type="checkbox"
            checked={isInsuranceCheck}
            onChange={(e) => setIsInsuranceCheck(e.target.checked)}
            className="mt-0.5 h-4 w-4 rounded border-zinc-700 bg-zinc-900"
          />
          <span>
            This is the insurance check
            <span className="mt-0.5 block text-[11px] text-zinc-500">
              The fee a client pays you up front to have their policy looked at.
              Ringing it up logs the amount and the day onto their insurance sheet.
            </span>
          </span>
        </label>

        <label className="flex items-start gap-2 text-sm text-zinc-300">
          <input
            type="checkbox"
            checked={creditsBack}
            onChange={(e) => setCreditsBack(e.target.checked)}
            className="mt-0.5 h-4 w-4 rounded border-zinc-700 bg-zinc-900"
          />
          <span>
            The fee comes back as account credit
            <span className="mt-0.5 block text-[11px] text-zinc-500">
              For a consultation whose fee goes toward the treatment. The moment it&rsquo;s paid for,
              the same amount lands in the client&rsquo;s account balance, ready to come off their next
              bill - same day or a month later.
            </span>
          </span>
        </label>

        <div>
          <Label htmlFor="svc-msg">Message sent when this is booked</Label>
          <Textarea
            id="svc-msg"
            value={bookingMessage}
            onChange={(e) => setBookingMessage(e.target.value)}
            placeholder="e.g. what to eat beforehand, what to bring, parking. Leave empty to send nothing extra."
            rows={4}
          />
          <FieldHint>
            Texted to the client right after their booking confirmation (emailed if there's no mobile number), for online and front-desk bookings alike. Not sent when you book quietly. You can use {"{{first_name}}"}, {"{{date}}"} and {"{{time}}"}.
          </FieldHint>
        </div>

        {!isAddon && addons.length > 0 && (
          <CheckGroup
            title="Add-ons offered with it"
            hint="Ticked add-ons appear as buttons under this treatment when you book it."
          >
            {addons.map((a) => (
              <label key={a.id} className="flex cursor-pointer items-center gap-2 text-sm text-zinc-300">
                <input
                  type="checkbox"
                  className={CHECKBOX}
                  checked={addonIds.includes(a.id)}
                  onChange={() => toggleId(addonIds, setAddonIds, a.id)}
                />
                {a.name}
                <span className="text-xs text-zinc-500">{formatMoney(a.price_cents)}</span>
              </label>
            ))}
          </CheckGroup>
        )}

        <CheckGroup
          title="Who can perform it"
          hint={providers.length === 0 ? "Add providers on the Providers tab first." : undefined}
        >
          {providers.map((p) => (
            <label key={p.id} className="flex cursor-pointer items-center gap-2 text-sm text-zinc-300">
              <input
                type="checkbox"
                className={CHECKBOX}
                checked={providerIds.includes(p.id)}
                onChange={() => toggleId(providerIds, setProviderIds, p.id)}
              />
              <span className={cn("h-2 w-2 rounded-full", colorSwatchClass(p.color))} />
              {p.name}
              {p.title && <span className="text-xs text-zinc-500">{p.title}</span>}
            </label>
          ))}
        </CheckGroup>

        {requiresRoom && (
          <CheckGroup
            title="Which rooms it can use"
            hint={
              rooms.length === 0
                ? "Add rooms on the Rooms tab first."
                : roomIds.length === 0
                  ? "None checked — any active room works."
                  : undefined
            }
          >
            {rooms.map((r) => (
              <label key={r.id} className="flex cursor-pointer items-center gap-2 text-sm text-zinc-300">
                <input
                  type="checkbox"
                  className={CHECKBOX}
                  checked={roomIds.includes(r.id)}
                  onChange={() => toggleId(roomIds, setRoomIds, r.id)}
                />
                {r.name}
                <span className="text-xs text-zinc-500">{roomKindLabel(r.kind)}</span>
              </label>
            ))}
          </CheckGroup>
        )}

        {/* What the folder sends on its own, before anything is ticked below.
            Shown first because it is what actually goes out most of the time. */}
        {packetForms.length > 0 && (
          <div className="rounded-xl border border-sky-500/25 bg-sky-500/5 px-3 py-2.5">
            <p className="text-xs font-semibold text-sky-200">
              Already sent with this treatment
            </p>
            <ul className="mt-1.5 space-y-0.5">
              {packetForms.map((f) => (
                <li key={f.id} className="flex items-center gap-1.5 text-sm text-zinc-200">
                  <Check className="h-3.5 w-3.5 shrink-0 text-sky-300" />
                  <span className="truncate">{f.name}</span>
                </li>
              ))}
            </ul>
            <p className="mt-1.5 text-[11px] leading-relaxed text-zinc-400">
              These go out on their own because of the treatment's paperwork folder.
              You do not need to tick them below - add something here only if this
              treatment needs a form the folder does not already send.
            </p>
          </div>
        )}

        <CheckGroup
          title="Extra forms, just for this treatment"
          hint={
            forms.length === 0
              ? "Create form templates on the Forms page first."
              : "On top of the folder above. A new client fills the full intake; returning clients only get the forms you mark “Every visit”."
          }
        >
          {forms.map((f) => {
            const checked = formIds.includes(f.id);
            return (
              <div key={f.id} className="flex items-center gap-2">
                <label className="flex min-w-0 flex-1 cursor-pointer items-center gap-2 text-sm text-zinc-300">
                  <input
                    type="checkbox"
                    className={CHECKBOX}
                    checked={checked}
                    onChange={() => toggleId(formIds, setFormIds, f.id)}
                  />
                  <span className="truncate">{f.name}</span>
                  <Badge color={formKindColor[f.kind] ?? "zinc"} size="xs">{f.kind}</Badge>
                </label>
                {checked && (
                  <Select
                    value={freqFor(f.id)}
                    onChange={(e) => setFormFreq((prev) => ({ ...prev, [f.id]: e.target.value as FormFrequency }))}
                    className="h-7 w-auto shrink-0 text-xs"
                    aria-label={`How often to send ${f.name}`}
                  >
                    <option value="once">First time only</option>
                    <option value="every_visit">Every visit</option>
                  </Select>
                )}
              </div>
            );
          })}
        </CheckGroup>

        <div className="flex items-center justify-end gap-2 border-t border-zinc-800 pt-4">
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button loading={saving} onClick={() => void save()}>{isNew ? "Create service" : "Save changes"}</Button>
        </div>
      </div>
    </Drawer>
  );
}

/* ═══ Rooms tab ════════════════════════════════════════════════════════════ */

function RoomsTab() {
  const toast = useToast();
  const confirm = useConfirm();
  const [rooms, setRooms] = useState<Room[]>([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState<Room | "new" | null>(null);

  const load = useCallback(async () => {
    try {
      const d = await api.get<{ rooms: Room[] }>("/rooms", { all: 1 });
      setRooms(d.rooms);
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => { void load(); }, [load]);

  const setActive = async (room: Room, active: boolean) => {
    const ok = await confirm({
      title: active ? `Reactivate ${room.name}?` : `Retire ${room.name}?`,
      description: active
        ? "It becomes bookable again for the services that use it."
        : "New appointments can't use it — existing bookings are untouched.",
      confirmLabel: active ? "Reactivate" : "Retire",
      danger: !active,
    });
    if (!ok) return;
    try {
      await api.patch(`/rooms/${room.id}`, { active });
      toast.success(active ? "Room reactivated" : "Room retired");
      void load();
    } catch (err) {
      toast.error(errMsg(err));
    }
  };

  if (loading) {
    return <div className="space-y-2">{Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-16" />)}</div>;
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-end">
        <Button size="sm" leftIcon={<Plus className="h-3.5 w-3.5" />} onClick={() => setEditing("new")}>
          New room
        </Button>
      </div>

      {rooms.length === 0 ? (
        <EmptyState
          icon={DoorOpen}
          title="No rooms yet"
          description="Rooms are real, schedulable resources — a facial room, a laser suite, even a single device. Booking a service holds the room so nothing double-books."
          action={<Button size="sm" leftIcon={<Plus className="h-3.5 w-3.5" />} onClick={() => setEditing("new")}>New room</Button>}
        />
      ) : (
        <div className="overflow-hidden rounded-xl border border-zinc-800/70 bg-zinc-900/30">
          {rooms.map((r, i) => (
            <div
              key={r.id}
              className={cn(
                "group flex items-center gap-3 px-4 py-3 hover:bg-zinc-900/40",
                i > 0 && "border-t border-zinc-800/40",
                !r.active && "opacity-60"
              )}
            >
              <DoorOpen className="h-4 w-4 shrink-0 text-zinc-500" />
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  <span className={cn("h-2.5 w-2.5 shrink-0 rounded-full", colorSwatchClass(r.color))} />
                  <span className="truncate text-sm font-medium text-zinc-100">{r.name}</span>
                  <Badge color="zinc" size="xs">{roomKindLabel(r.kind)}</Badge>
                  {r.slot_minutes && <Badge color="sky" size="xs">Times every {r.slot_minutes} min</Badge>}
                  {r.area && <Badge color="zinc" size="xs">{r.area}</Badge>}
                  {r.online_booking === false && <Badge color="amber" size="xs">Front desk only</Badge>}
                  {roomMates(r, rooms).length > 0 && (
                    <Badge color="violet" size="xs">Shares the room with {roomMates(r, rooms).map((m) => m.name).join(", ")}</Badge>
                  )}
                  {!r.active && <Badge color="amber" size="xs">Retired</Badge>}
                </div>
                {r.description && <p className="mt-0.5 truncate text-xs text-zinc-500">{r.description}</p>}
              </div>
              <div className="flex shrink-0 items-center gap-1 opacity-0 transition-opacity group-hover:opacity-100 focus-within:opacity-100">
                <button
                  type="button"
                  onClick={() => setEditing(r)}
                  className="inline-flex h-7 w-7 items-center justify-center rounded text-zinc-400 hover:bg-zinc-800 hover:text-zinc-200"
                  aria-label={`Edit ${r.name}`}
                >
                  <Pencil className="h-3.5 w-3.5" />
                </button>
                {r.active ? (
                  <button
                    type="button"
                    onClick={() => void setActive(r, false)}
                    className="inline-flex h-7 w-7 items-center justify-center rounded text-zinc-400 hover:bg-zinc-800 hover:text-rose-300"
                    aria-label={`Retire ${r.name}`}
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                ) : (
                  <Button variant="outline" size="sm" onClick={() => void setActive(r, true)}>
                    Reactivate
                  </Button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {editing !== null && (
        <RoomModal
          room={editing}
          rooms={rooms}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); void load(); }}
        />
      )}
    </div>
  );
}

/** The other active seats in the same room (same privacy group). */
function roomMates(room: Room, rooms: Room[]): Room[] {
  if (!room.privacy_group) return [];
  return rooms.filter((o) => o.id !== room.id && o.active && o.privacy_group === room.privacy_group);
}

function RoomModal({
  room, rooms, onClose, onSaved,
}: {
  room: Room | "new";
  rooms: Room[];
  onClose: () => void;
  onSaved: () => void;
}) {
  const toast = useToast();
  const isNew = room === "new";
  const base = isNew ? null : room;
  const [name, setName] = useState(base?.name ?? "");
  const [kind, setKind] = useState(base?.kind ?? "treatment_room");
  const [description, setDescription] = useState(base?.description ?? "");
  const [color, setColor] = useState(base?.color ?? "zinc");
  const [slotMinutes, setSlotMinutes] = useState<string>(base?.slot_minutes ? String(base.slot_minutes) : "");
  const [onlineBooking, setOnlineBooking] = useState(base?.online_booking !== false);
  const [area, setArea] = useState(base?.area ?? "");
  const [notifyEmail, setNotifyEmail] = useState(base?.notify_email ?? "");
  const [sharesWith, setSharesWith] = useState<string>(base ? (roomMates(base, rooms)[0]?.id ?? "") : "");
  const [saving, setSaving] = useState(false);
  const others = rooms.filter((o) => o.active && o.id !== base?.id);

  const save = async () => {
    if (!name.trim()) { toast.error("Give the room a name"); return; }
    const body = {
      name: name.trim(), kind, description: description.trim() || null, color,
      slot_minutes: slotMinutes ? Number(slotMinutes) : null,
      shares_room_with: sharesWith || null,
      online_booking: onlineBooking,
      area: area.trim() || null,
      notify_email: notifyEmail.trim() || null,
    };
    setSaving(true);
    try {
      if (isNew) {
        await api.post("/rooms", body);
        toast.success("Room created");
      } else {
        await api.patch(`/rooms/${base!.id}`, body);
        toast.success("Room updated");
      }
      onSaved();
    } catch (err) {
      toast.error(errMsg(err));
      setSaving(false);
    }
  };

  return (
    <Modal open onClose={onClose} title={isNew ? "New room" : "Edit room"} size="sm">
      <div className="space-y-4">
        <div>
          <div className="flex items-end gap-2">
            <div className="flex-1">
              <Label htmlFor="room-name" required>Name</Label>
              <Input id="room-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Facial Room 1" autoFocus />
            </div>
            <ColorSwatchPicker value={color} onChange={setColor} />
          </div>
          <FieldHint>Each seat gets its own colour on the calendar.</FieldHint>
        </div>
        <div>
          <Label htmlFor="room-kind">Kind</Label>
          <Select id="room-kind" value={kind} onChange={(e) => setKind(e.target.value)}>
            {ROOM_KINDS.map((k) => <option key={k.value} value={k.value}>{k.label}</option>)}
          </Select>
        </div>
        <div>
          <Label htmlFor="room-slot">Booking times</Label>
          <Select id="room-slot" value={slotMinutes} onChange={(e) => setSlotMinutes(e.target.value)}>
            <option value="">Every {bookingGridMinutes()} minutes (the usual)</option>
            <option value="15">Every 15 minutes (9:00, 9:15, 9:30 …)</option>
            <option value="30">Every 30 minutes (9:00, 9:30, 10:00 …)</option>
            <option value="60">On the hour</option>
          </Select>
          <FieldHint>Phone, video and pick-up seats can take quarter hours without touching a chair.</FieldHint>
        </div>
        <div>
          <Label htmlFor="room-shares">Shares the room with</Label>
          <Select id="room-shares" value={sharesWith} onChange={(e) => setSharesWith(e.target.value)}>
            <option value="">Nobody (its own space)</option>
            {others.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
          </Select>
          <FieldHint>Seats in the same room are never booked at the same time, so a private consult never sits beside a drip.</FieldHint>
        </div>
        <div>
          <Label htmlFor="room-area">Part of the clinic</Label>
          <Input id="room-area" value={area} onChange={(e) => setArea(e.target.value)} placeholder="e.g. Sunroom, Lounge" />
          <FieldHint>Online booking lets one client start per area per time slot, so nobody has to start two drips at once.</FieldHint>
        </div>
        <div>
          {/* "Is it possible that when the client is scheduled for Lounge 2 we
              set the link up to be emailed to us? ... the email to the lounge
              is Heavenlyhydration@ivloud.com" */}
          <Label htmlFor="room-notify">Email the video link here too</Label>
          <Input
            id="room-notify"
            type="email"
            value={notifyEmail}
            onChange={(e) => setNotifyEmail(e.target.value)}
            placeholder="e.g. Heavenlyhydration@ivloud.com"
          />
          <FieldHint>When a video visit in this seat gets its join link, a copy goes here so whoever is in the room can join. Leave it empty for none.</FieldHint>
        </div>
        <label className="flex cursor-pointer items-start gap-2 text-sm text-zinc-300">
          <input
            type="checkbox"
            checked={onlineBooking}
            onChange={(e) => setOnlineBooking(e.target.checked)}
            className="mt-0.5 h-4 w-4 rounded border-zinc-600 accent-indigo-500"
          />
          <span>
            Clients can book this seat online
            <span className="block text-[11px] text-zinc-500">Off means only the front desk can put someone here.</span>
          </span>
        </label>
        <div>
          <Label htmlFor="room-desc">Description</Label>
          <Textarea id="room-desc" value={description} onChange={(e) => setDescription(e.target.value)} rows={2} placeholder="Optional: equipment, quirks, notes for staff." />
        </div>
        <ModalFooter>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button loading={saving} onClick={() => void save()}>{isNew ? "Create" : "Save"}</Button>
        </ModalFooter>
      </div>
    </Modal>
  );
}

/* ═══ Providers tab ════════════════════════════════════════════════════════ */

function scheduleSummary(schedule: WeeklySchedule): string {
  const parts = DAYS.flatMap(({ key, label }) => {
    const wins = schedule[key];
    if (!wins || wins.length === 0) return [];
    return [`${label} ${wins.map((w) => `${w.start}–${w.end}`).join(", ")}`];
  });
  return parts.length > 0 ? parts.join(" · ") : "No weekly hours set";
}

function ProvidersTab() {
  const toast = useToast();
  const confirm = useConfirm();
  const [providers, setProviders] = useState<Provider[]>([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState<Provider | "new" | null>(null);

  const load = useCallback(async () => {
    try {
      const d = await api.get<{ providers: Provider[] }>("/providers", { all: 1 });
      setProviders(d.providers);
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => { void load(); }, [load]);

  const deactivate = async (p: Provider) => {
    const ok = await confirm({
      title: `Deactivate ${p.name}?`,
      description: "They stop appearing in booking and on the calendar. Existing appointments are untouched.",
      confirmLabel: "Deactivate",
      danger: true,
    });
    if (!ok) return;
    try {
      await api.delete(`/providers/${p.id}`);
      toast.success("Provider deactivated");
      void load();
    } catch (err) {
      toast.error(errMsg(err));
    }
  };

  const reactivate = async (p: Provider) => {
    try {
      await api.patch(`/providers/${p.id}`, { active: true });
      toast.success("Provider reactivated");
      void load();
    } catch (err) {
      toast.error(errMsg(err));
    }
  };

  if (loading) {
    return (
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {Array.from({ length: 3 }).map((_, i) => <Skeleton key={i} className="h-32" />)}
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-end">
        <Button size="sm" leftIcon={<Plus className="h-3.5 w-3.5" />} onClick={() => setEditing("new")}>
          New provider
        </Button>
      </div>

      {providers.length === 0 ? (
        <EmptyState
          icon={Users}
          title="No providers yet"
          description="Providers are the people who perform treatments. Each gets their own weekly hours, color, and time off."
          action={<Button size="sm" leftIcon={<Plus className="h-3.5 w-3.5" />} onClick={() => setEditing("new")}>New provider</Button>}
        />
      ) : (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {providers.map((p) => (
            <div key={p.id} className={cn(CARD, "group flex flex-col gap-2", !p.active && "opacity-60")}>
              <div className="flex items-start justify-between gap-2">
                <div className="flex min-w-0 items-center gap-2">
                  <span className={cn("h-2.5 w-2.5 shrink-0 rounded-full", colorSwatchClass(p.color))} />
                  <div className="min-w-0">
                    <div className="truncate text-sm font-medium text-zinc-100">{p.name}</div>
                    {p.title && <div className="truncate text-xs text-zinc-500">{p.title}</div>}
                  </div>
                </div>
                <div className="flex shrink-0 items-center gap-1 opacity-0 transition-opacity group-hover:opacity-100 focus-within:opacity-100">
                  <button
                    type="button"
                    onClick={() => setEditing(p)}
                    className="inline-flex h-7 w-7 items-center justify-center rounded text-zinc-400 hover:bg-zinc-800 hover:text-zinc-200"
                    aria-label={`Edit ${p.name}`}
                  >
                    <Pencil className="h-3.5 w-3.5" />
                  </button>
                  {p.active && (
                    <button
                      type="button"
                      onClick={() => void deactivate(p)}
                      className="inline-flex h-7 w-7 items-center justify-center rounded text-zinc-400 hover:bg-zinc-800 hover:text-rose-300"
                      aria-label={`Deactivate ${p.name}`}
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  )}
                </div>
              </div>
              <div className="flex flex-wrap items-center gap-1.5">
                {p.bookable && <Badge color="emerald" size="xs">Bookable online</Badge>}
                {!p.active && <Badge color="amber" size="xs">Inactive</Badge>}
              </div>
              <p className="text-xs leading-relaxed text-zinc-500">{scheduleSummary(p.schedule)}</p>
              {!p.active && (
                <Button variant="outline" size="sm" className="self-start" onClick={() => void reactivate(p)}>
                  Reactivate
                </Button>
              )}
            </div>
          ))}
        </div>
      )}

      {editing !== null && (
        <ProviderDrawer
          provider={editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); void load(); }}
        />
      )}
    </div>
  );
}

/* ─── Provider drawer ──────────────────────────────────────────────────────── */

const TIME_INPUT =
  "h-8 rounded-md border border-zinc-800 bg-zinc-900/50 px-2 text-xs text-zinc-100 [color-scheme:dark] " +
  "focus:border-indigo-500/50 focus:bg-zinc-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500/30";

type DraftSchedule = Record<DayKey, { start: string; end: string }[]>;

function toDraftSchedule(s: WeeklySchedule | undefined): DraftSchedule {
  const out = {} as DraftSchedule;
  for (const { key } of DAYS) out[key] = (s?.[key] ?? []).map((w) => ({ ...w }));
  return out;
}

function ProviderDrawer({
  provider, onClose, onSaved,
}: {
  provider: Provider | "new";
  onClose: () => void;
  onSaved: () => void;
}) {
  const toast = useToast();
  const confirm = useConfirm();
  const isNew = provider === "new";
  const base = isNew ? null : provider;

  const [name, setName] = useState(base?.name ?? "");
  const [title, setTitle] = useState(base?.title ?? "");
  const [email, setEmail] = useState(base?.email ?? "");
  const [phone, setPhone] = useState(base?.phone ?? "");
  const [color, setColor] = useState(base?.color ?? "indigo");
  const [bookable, setBookable] = useState(base?.bookable ?? true);
  // "we have been using Google meet … how would the provider have access" —
  // their own standing room, so they are always already in it.
  const [meetingUrl, setMeetingUrl] = useState(base?.meeting_url ?? "");
  const [schedule, setSchedule] = useState<DraftSchedule>(() => toDraftSchedule(base?.schedule));
  const [saving, setSaving] = useState(false);

  // Time off — only meaningful once the provider exists.
  const [timeOff, setTimeOff] = useState<ProviderTimeOff[]>([]);
  const [offStart, setOffStart] = useState("");
  const [offEnd, setOffEnd] = useState("");
  const [offReason, setOffReason] = useState("");
  const [addingOff, setAddingOff] = useState(false);

  const loadTimeOff = useCallback(async () => {
    if (!base?.id) return;
    try {
      const d = await api.get<{ time_off: ProviderTimeOff[] }>(`/providers/${base.id}/time-off`);
      setTimeOff(d.time_off);
    } catch (err) {
      toast.error(errMsg(err));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [base?.id]);
  useEffect(() => { void loadTimeOff(); }, [loadTimeOff]);

  const upcomingOff = useMemo(
    () =>
      timeOff
        .filter((t) => new Date(t.ends_at).getTime() >= Date.now())
        .sort((a, b) => new Date(a.starts_at).getTime() - new Date(b.starts_at).getTime()),
    [timeOff]
  );

  const setWindow = (day: DayKey, idx: number, field: "start" | "end", value: string) => {
    setSchedule((prev) => ({
      ...prev,
      [day]: prev[day].map((w, i) => (i === idx ? { ...w, [field]: value } : w)),
    }));
  };
  const addWindow = (day: DayKey) => {
    setSchedule((prev) => ({ ...prev, [day]: [...prev[day], { start: "09:00", end: "17:00" }] }));
  };
  const removeWindow = (day: DayKey, idx: number) => {
    setSchedule((prev) => ({ ...prev, [day]: prev[day].filter((_, i) => i !== idx) }));
  };

  const save = async () => {
    if (!name.trim()) { toast.error("Give the provider a name"); return; }
    const cleaned: WeeklySchedule = {};
    for (const { key } of DAYS) {
      const wins = schedule[key].filter((w) => w.start && w.end);
      if (wins.length > 0) cleaned[key] = wins;
    }
    const body = {
      name: name.trim(),
      title: title.trim() || null,
      email: email.trim() || null,
      phone: phone.trim() || null,
      color,
      schedule: cleaned,
      bookable,
      meeting_url: meetingUrl.trim() || null,
    };
    setSaving(true);
    try {
      if (isNew) {
        await api.post("/providers", body);
        toast.success("Provider created");
      } else {
        await api.patch(`/providers/${base!.id}`, body);
        toast.success("Provider updated");
      }
      onSaved();
    } catch (err) {
      toast.error(errMsg(err));
      setSaving(false);
    }
  };

  const addTimeOff = async () => {
    if (!base?.id) return;
    if (!offStart || !offEnd) { toast.error("Pick both a start and an end"); return; }
    const starts = new Date(offStart);
    const ends = new Date(offEnd);
    if (ends.getTime() <= starts.getTime()) { toast.error("Time off must end after it starts"); return; }
    setAddingOff(true);
    try {
      await api.post(`/providers/${base.id}/time-off`, {
        starts_at: starts.toISOString(),
        ends_at: ends.toISOString(),
        reason: offReason.trim() || null,
      });
      setOffStart(""); setOffEnd(""); setOffReason("");
      toast.success("Time off added");
      void loadTimeOff();
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setAddingOff(false);
    }
  };

  const removeTimeOff = async (off: ProviderTimeOff) => {
    if (!base?.id) return;
    const ok = await confirm({
      title: "Remove this time off?",
      description: "Their availability opens back up for these dates.",
      confirmLabel: "Remove",
      danger: true,
    });
    if (!ok) return;
    try {
      await api.delete(`/providers/${base.id}/time-off/${off.id}`);
      toast.success("Time off removed");
      void loadTimeOff();
    } catch (err) {
      toast.error(errMsg(err));
    }
  };

  return (
    <Drawer open onClose={onClose} title={isNew ? "New provider" : `Edit ${base?.name}`} width="lg">
      <div className="space-y-4 p-5">
        <div className="flex items-end gap-2">
          <div className="flex-1">
            <Label htmlFor="prov-name" required>Name</Label>
            <Input id="prov-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Debra Kelly" autoFocus />
          </div>
          <ColorSwatchPicker value={color} onChange={setColor} />
        </div>

        <div>
          <Label htmlFor="prov-title">Title</Label>
          <Input id="prov-title" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="e.g. Nurse Injector" />
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label htmlFor="prov-email">Email</Label>
            <Input id="prov-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="name@example.com" />
          </div>
          <div>
            <Label htmlFor="prov-phone">Phone</Label>
            <Input id="prov-phone" type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="+1 (555) 555-1234" />
          </div>
        </div>

        <label className="flex cursor-pointer items-center gap-2 text-sm text-zinc-300">
          <input type="checkbox" className={CHECKBOX} checked={bookable} onChange={(e) => setBookable(e.target.checked)} />
          Bookable online
        </label>

        <div>
          <Label htmlFor="prov-meet">Their video room</Label>
          <Input
            id="prov-meet"
            type="url"
            value={meetingUrl}
            onChange={(e) => setMeetingUrl(e.target.value)}
            placeholder="https://meet.google.com/abc-defg-hij"
          />
          <FieldHint>
            Their own standing meeting link, so they can always get in. Clients booked for a video
            consultation are texted this a few minutes before it starts - never days ahead.
            Leave blank and nothing is sent.
          </FieldHint>
        </div>

        <div className="rounded-lg border border-zinc-800 p-3">
          <h3 className={cn(SECTION_HEADING, "mb-3")}>Weekly hours</h3>
          <div className="space-y-2.5">
            {DAYS.map(({ key, label }) => (
              <div key={key} className="flex items-start gap-3">
                <div className="w-10 shrink-0 pt-1.5 text-xs font-medium text-zinc-400">{label}</div>
                <div className="flex-1 space-y-1.5">
                  {schedule[key].length === 0 && <div className="pt-1.5 text-xs text-zinc-600">Off</div>}
                  {schedule[key].map((w, i) => (
                    <div key={i} className="flex items-center gap-2">
                      <input
                        type="time"
                        value={w.start}
                        onChange={(e) => setWindow(key, i, "start", e.target.value)}
                        className={TIME_INPUT}
                        aria-label={`${label} start`}
                      />
                      <span className="text-xs text-zinc-600">–</span>
                      <input
                        type="time"
                        value={w.end}
                        onChange={(e) => setWindow(key, i, "end", e.target.value)}
                        className={TIME_INPUT}
                        aria-label={`${label} end`}
                      />
                      <button
                        type="button"
                        onClick={() => removeWindow(key, i)}
                        className="inline-flex h-6 w-6 items-center justify-center rounded text-zinc-500 hover:bg-zinc-800 hover:text-zinc-200"
                        aria-label={`Remove ${label} hours`}
                      >
                        <X className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  ))}
                  <button
                    type="button"
                    onClick={() => addWindow(key)}
                    className="text-xs text-indigo-400 hover:text-indigo-300"
                  >
                    + Add hours
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="rounded-lg border border-zinc-800 p-3">
          <h3 className={cn(SECTION_HEADING, "mb-3")}>Time off</h3>
          {isNew ? (
            <p className="text-xs text-zinc-500">Save the provider first, then add vacations and blocked days here.</p>
          ) : (
            <div className="space-y-3">
              {upcomingOff.length === 0 ? (
                <p className="flex items-center gap-2 text-xs text-zinc-500">
                  <CalendarOff className="h-3.5 w-3.5" />
                  No upcoming time off.
                </p>
              ) : (
                <div className="space-y-1.5">
                  {upcomingOff.map((t) => (
                    <div key={t.id} className="flex items-center gap-2 rounded-md bg-zinc-900/50 px-2.5 py-1.5 text-xs">
                      <span className="text-zinc-300">
                        {formatDateTime(t.starts_at)} <span className="text-zinc-600">→</span> {formatDateTime(t.ends_at)}
                      </span>
                      {t.reason && <span className="truncate text-zinc-500">{t.reason}</span>}
                      <button
                        type="button"
                        onClick={() => void removeTimeOff(t)}
                        className="ml-auto inline-flex h-6 w-6 shrink-0 items-center justify-center rounded text-zinc-500 hover:bg-zinc-800 hover:text-rose-300"
                        aria-label="Remove time off"
                      >
                        <X className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
              <div className="space-y-2 border-t border-zinc-800/70 pt-3">
                <div className="grid grid-cols-2 gap-2">
                  <DateTimePicker value={offStart} onChange={setOffStart} placeholder="Starts" />
                  <DateTimePicker value={offEnd} onChange={setOffEnd} placeholder="Ends" />
                </div>
                <div className="flex items-center gap-2">
                  <Input
                    value={offReason}
                    onChange={(e) => setOffReason(e.target.value)}
                    placeholder="Reason (optional)"
                    className="h-8 text-xs"
                  />
                  <Button variant="secondary" size="sm" loading={addingOff} onClick={() => void addTimeOff()}>
                    Add
                  </Button>
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="flex items-center justify-end gap-2 border-t border-zinc-800 pt-4">
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button loading={saving} onClick={() => void save()}>{isNew ? "Create provider" : "Save changes"}</Button>
        </div>
      </div>
    </Drawer>
  );
}

/* ═══ Packages tab ═════════════════════════════════════════════════════════ */

/** "3 IV drips", "2 of any wellness injection, posted out". */
function lineSummary(it: PackageItem): string {
  const n = it.sessions ?? 1;
  const names = (it.services ?? []).map((x) => x.name);
  const what = it.label?.trim()
    || it.product_name
    || (names.length === 1 ? names[0]!
      : names.length > 1 ? `of ${names.length} treatments`
        : it.service_name ?? "any treatment");
  const how = it.fulfilment === "take_home" ? ", taken home"
    : it.fulfilment === "mail_out" ? ", posted out" : "";
  return `${n} ${what}${how}`;
}

function PackagesTab() {
  const toast = useToast();
  const confirm = useConfirm();
  const [packages, setPackages] = useState<SpaPackage[]>([]);
  const [services, setServices] = useState<Service[]>([]);
  const [products, setProducts] = useState<Product[]>([]);
  const [categories, setCategories] = useState<PackageCategory[]>([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState<SpaPackage | "new" | null>(null);
  const [editingCategory, setEditingCategory] = useState<PackageCategory | "new" | null>(null);

  const load = useCallback(async () => {
    try {
      const [pkgs, svcs, cats, prods] = await Promise.all([
        api.get<{ packages: SpaPackage[] }>("/pos/packages"),
        api.get<{ services: Service[] }>("/services"),
        api.get<{ categories: PackageCategory[] }>("/pos/package-categories"),
        // "add the ability to add products to packages and memberships"
        api.get<{ products: Product[] }>("/payments/products"),
      ]);
      setPackages(pkgs.packages.filter((p) => !p.archived));
      setServices(svcs.services.filter((s) => !s.archived));
      setCategories(cats.categories);
      setProducts(prods.products.filter((x) => !x.archived && x.pricing_type !== "subscription"));
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => { void load(); }, [load]);

  /** Her headings in her order, then anything not filed under one yet. */
  const grouped = useMemo(() => {
    const out = categories.map((c) => ({
      key: c.id, label: c.name,
      list: packages.filter((p) => p.category_id === c.id),
    })).filter((g) => g.list.length > 0);
    const loose = packages.filter((p) => !p.category_id || !categories.some((c) => c.id === p.category_id));
    if (loose.length > 0) out.push({ key: "none", label: "Not in a category", list: loose });
    return out;
  }, [packages, categories]);

  const archive = async (pkg: SpaPackage) => {
    const ok = await confirm({
      title: `Archive ${pkg.name}?`,
      description: "It can't be sold anymore. Clients keep any sessions they've already bought.",
      confirmLabel: "Archive",
      danger: true,
    });
    if (!ok) return;
    try {
      await api.patch(`/pos/packages/${pkg.id}`, { archived: true });
      toast.success("Package archived");
      void load();
    } catch (err) {
      toast.error(errMsg(err));
    }
  };

  if (loading) {
    return <div className="space-y-2">{Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-16" />)}</div>;
  }

  return (
    <div className="space-y-3">
      {/* "can you add a new category Butterfly Pushes in packages" - the
          treatments tab has always had this button; packages never did, so
          every new heading had to be asked for. */}
      <div className="flex items-center justify-end gap-2">
        <Button variant="secondary" size="sm" leftIcon={<Plus className="h-3.5 w-3.5" />} onClick={() => setEditingCategory("new")}>
          New category
        </Button>
        <Button size="sm" leftIcon={<Plus className="h-3.5 w-3.5" />} onClick={() => setEditing("new")}>
          New package
        </Button>
      </div>

      {packages.length === 0 ? (
        <EmptyState
          icon={Package}
          title="No packages yet"
          description="Sell a bundle of sessions up front — like 6 laser treatments — and check clients in against their balance at each visit."
          action={<Button size="sm" leftIcon={<Plus className="h-3.5 w-3.5" />} onClick={() => setEditing("new")}>New package</Button>}
        />
      ) : (
        /* "can we seperate the area under the packages tab to create
            categories" - her list is long enough that finding one meant
            reading all of it. Anything not filed yet sits under its own
            heading rather than disappearing. */
        grouped.map(({ key, label, list }) => (
        <div key={key} className="space-y-1.5">
          <h3 className="flex items-center gap-1.5 px-1 text-xs font-semibold uppercase tracking-wider text-zinc-500">
            {label} <span className="font-normal normal-case text-zinc-600">{list.length}</span>
            {key !== "none" && (
              <button
                type="button"
                onClick={() => {
                  const cat = categories.find((c) => c.id === key);
                  if (cat) setEditingCategory(cat);
                }}
                className="rounded p-0.5 text-zinc-600 hover:bg-zinc-800 hover:text-zinc-300"
                aria-label={`Edit the ${label} category`}
              >
                <Pencil className="h-3 w-3" />
              </button>
            )}
          </h3>
        <div className="overflow-hidden rounded-xl border border-zinc-800/70 bg-zinc-900/30">
          {list.map((p, i) => {
            const perSession = p.sessions > 0 ? Math.round(p.price_cents / p.sessions) : p.price_cents;
            return (
              <div
                key={p.id}
                className={cn("group flex items-center gap-3 px-4 py-3 hover:bg-zinc-900/40", i > 0 && "border-t border-zinc-800/40")}
              >
                <Package className="h-4 w-4 shrink-0 text-zinc-500" />
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <span className="truncate text-sm font-medium text-zinc-100">{p.name}</span>
                    {p.take_home && <Badge color="teal" size="xs">Can take home</Badge>}
                  </div>
                  {/* A combo reads as its lines - "3 IV drips · 2 wellness
                      injections, posted" - because that is what she is
                      selling, not a session count. */}
                  <p className="mt-0.5 text-xs text-zinc-500">
                    {(p.items ?? []).length > 0
                      ? (p.items ?? []).map(lineSummary).join(" · ")
                      : `${p.service_name ?? "Any service"} · ${p.sessions} ${p.sessions === 1 ? "session" : "sessions"}`}
                  </p>
                </div>
                <div className="shrink-0 text-right">
                  <div className="text-sm font-medium text-zinc-200 tabular-nums">{formatMoney(p.price_cents)}</div>
                  <div className="text-xs text-zinc-500 tabular-nums">{formatMoney(perSession)}/session</div>
                </div>
                <div className="flex shrink-0 items-center gap-1 opacity-0 transition-opacity group-hover:opacity-100 focus-within:opacity-100">
                  <button
                    type="button"
                    onClick={() => setEditing(p)}
                    className="inline-flex h-7 w-7 items-center justify-center rounded text-zinc-400 hover:bg-zinc-800 hover:text-zinc-200"
                    aria-label={`Edit ${p.name}`}
                  >
                    <Pencil className="h-3.5 w-3.5" />
                  </button>
                  <button
                    type="button"
                    onClick={() => void archive(p)}
                    className="inline-flex h-7 w-7 items-center justify-center rounded text-zinc-400 hover:bg-zinc-800 hover:text-rose-300"
                    aria-label={`Archive ${p.name}`}
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
        </div>
        ))
      )}

      {editing !== null && (
        <PackageModal
          pkg={editing}
          services={services}
          products={products}
          categories={categories}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); void load(); }}
        />
      )}
      {editingCategory !== null && (
        <PackageCategoryModal
          category={editingCategory}
          onClose={() => setEditingCategory(null)}
          onSaved={() => { setEditingCategory(null); void load(); }}
        />
      )}
    </div>
  );
}

/** A heading on the Packages tab. Same shape as the treatment one. */
function PackageCategoryModal({
  category, onClose, onSaved,
}: {
  category: PackageCategory | "new";
  onClose: () => void;
  onSaved: () => void;
}) {
  const toast = useToast();
  const confirm = useConfirm();
  const isNew = category === "new";
  const base = isNew ? null : category;
  const [name, setName] = useState(base?.name ?? "");
  const [color, setColor] = useState(base?.color ?? "violet");
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!name.trim()) { toast.error("Give the category a name"); return; }
    setSaving(true);
    try {
      if (isNew) {
        await api.post("/pos/package-categories", { name: name.trim(), color });
        toast.success("Category created");
      } else {
        await api.patch(`/pos/package-categories/${base!.id}`, { name: name.trim(), color });
        toast.success("Category updated");
      }
      onSaved();
    } catch (err) {
      toast.error(errMsg(err));
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!base) return;
    const ok = await confirm({
      title: `Delete ${base.name}?`,
      description: "The packages in it are kept - they just move back to Not in a category.",
      confirmLabel: "Delete",
      danger: true,
    });
    if (!ok) return;
    try {
      await api.delete(`/pos/package-categories/${base.id}`);
      toast.success("Category deleted");
      onSaved();
    } catch (err) {
      toast.error(errMsg(err));
    }
  };

  return (
    <Modal open onClose={onClose} title={isNew ? "New category" : "Edit category"} size="sm">
      <div className="space-y-4">
        <div className="flex items-end gap-2">
          <div className="flex-1">
            <Label htmlFor="pkg-cat-name" required>Name</Label>
            <Input
              id="pkg-cat-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Mail Out"
              autoFocus
              onKeyDown={(e) => { if (e.key === "Enter") void save(); }}
            />
          </div>
          <ColorSwatchPicker value={color} onChange={setColor} />
        </div>
        <ModalFooter>
          {!isNew && (
            <Button variant="ghost" className="mr-auto text-rose-300 hover:text-rose-200" onClick={() => void remove()}>
              Delete
            </Button>
          )}
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button loading={saving} onClick={() => void save()}>{isNew ? "Create" : "Save"}</Button>
        </ModalFooter>
      </div>
    </Modal>
  );
}

/**
 * Building one of her packages.
 *
 * "When we create pkgs it is either one or the other or it could be a combo of
 *  1-3 of different services… For example Lifestyle fit and fab has a combo of
 *  IV Drip and Wellness Injections… IV Drip how many and which drips (not all
 *  are included), Wellness injection I can pick what's included… Then price.
 *  Then we pick - Take home, Mail Out, Vouchers"
 *
 * So the form is her list, in her order: name, what the client reads, then one
 * line per kind of treatment - how many, which ones count, and how they get
 * them - then the price for the lot.
 */
const FULFILMENTS: { key: Fulfilment; label: string; blurb: string }[] = [
  { key: "in_lounge", label: "In the lounge", blurb: "A voucher to use at a visit" },
  { key: "take_home", label: "Take home", blurb: "Handed over at the counter when they buy it" },
  { key: "mail_out", label: "Posted out", blurb: "Shipped to them; marked as sent when it goes" },
];

interface DraftLine {
  key: string;
  serviceIds: string[];
  /**
   * A line that is a product off the shelf rather than a treatment.
   *
   * "add the ability to add products to packages and memberships"
   *
   * One or the other per line: set this and the treatment ticks are ignored,
   * which is what keeps a drip voucher from being spendable on a bottle.
   */
  productId: string;
  sessions: string;
  fulfilment: Fulfilment;
  label: string;
}

const newLine = (): DraftLine => ({
  key: Math.random().toString(36).slice(2),
  serviceIds: [], productId: "", sessions: "1", fulfilment: "in_lounge", label: "",
});

function PackageModal({
  pkg, services, products, categories, onClose, onSaved,
}: {
  pkg: SpaPackage | "new";
  services: Service[];
  products: Product[];
  categories: PackageCategory[];
  onClose: () => void;
  onSaved: () => void;
}) {
  const toast = useToast();
  const isNew = pkg === "new";
  const base = isNew ? null : pkg;
  const [name, setName] = useState(base?.name ?? "");
  const [categoryId, setCategoryId] = useState(base?.category_id ?? "");
  const [description, setDescription] = useState(base?.description ?? "");
  const [price, setPrice] = useState(base ? centsToDollars(base.price_cents) : "");
  const [lines, setLines] = useState<DraftLine[]>(() => {
    const existing = base?.items ?? [];
    if (existing.length > 0) {
      return existing.map((it) => ({
        key: it.id ?? Math.random().toString(36).slice(2),
        serviceIds: (it.services ?? []).length > 0
          ? (it.services ?? []).map((x) => x.id)
          : it.service_id ? [it.service_id] : [],
        productId: it.product_id ?? "",
        sessions: String(it.sessions ?? 1),
        fulfilment: it.fulfilment ?? "in_lounge",
        label: it.label ?? "",
      }));
    }
    // An older package held one treatment and a count on the package itself.
    if (base) {
      return [{
        key: "legacy",
        serviceIds: base.service_id ? [base.service_id] : [],
        productId: "",
        sessions: String(base.sessions ?? 1),
        fulfilment: base.take_home ? "take_home" : "in_lounge",
        label: "",
      }];
    }
    return [newLine()];
  });
  const [saving, setSaving] = useState(false);

  const setLine = (key: string, patch: Partial<DraftLine>) =>
    setLines((prev) => prev.map((l) => (l.key === key ? { ...l, ...patch } : l)));
  const toggleService = (key: string, id: string) =>
    setLines((prev) => prev.map((l) => l.key !== key ? l : {
      ...l,
      serviceIds: l.serviceIds.includes(id) ? l.serviceIds.filter((x) => x !== id) : [...l.serviceIds, id],
    }));

  const save = async () => {
    if (!name.trim()) { toast.error("Give the package a name"); return; }
    const items = [];
    for (const l of lines) {
      if (!l.productId && l.serviceIds.length === 0) {
        toast.error("Each line needs a treatment ticked, or a product picked"); return;
      }
      const n = parseInt(l.sessions, 10);
      if (!Number.isFinite(n) || n <= 0) { toast.error("How many must be at least 1"); return; }
      items.push({
        service_ids: l.productId ? [] : l.serviceIds,
        service_id: !l.productId && l.serviceIds.length === 1 ? l.serviceIds[0] : null,
        product_id: l.productId || null,
        sessions: n,
        fulfilment: l.fulfilment,
        label: l.label.trim() || null,
      });
    }
    const body = {
      name: name.trim(),
      description: description.trim() || null,
      // The package's own treatment and count stay in step with a single-line
      // package, so anything still reading them sees the same thing.
      service_id: items.length === 1 ? items[0]!.service_id : null,
      sessions: items.length === 1 ? items[0]!.sessions : items.reduce((a, b) => a + b.sessions, 0),
      price_cents: dollarsToCents(price),
      take_home: items.some((i) => i.fulfilment === "take_home"),
      category_id: categoryId || null,
      // What the till's question opens on. The desk can still answer any of
      // the three on the day, so this is a starting point, not a rule.
      default_fulfilment: items[0]?.fulfilment ?? "in_lounge",
      items,
    };
    setSaving(true);
    try {
      if (isNew) {
        await api.post("/pos/packages", body);
        toast.success("Package created");
      } else {
        await api.patch(`/pos/packages/${base!.id}`, body);
        toast.success("Package updated");
      }
      onSaved();
    } catch (err) {
      toast.error(errMsg(err));
      setSaving(false);
    }
  };

  return (
    <Modal open onClose={onClose} title={isNew ? "New package" : "Edit package"} size="lg">
      <div className="space-y-4">
        <div className="grid gap-3 sm:grid-cols-2">
          <div>
            <Label htmlFor="pkg-name" required>Name</Label>
            <Input id="pkg-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Lifestyle Fit and Fab" autoFocus />
          </div>
          <div>
            <Label htmlFor="pkg-cat">Category</Label>
            <Select id="pkg-cat" value={categoryId} onChange={(e) => setCategoryId(e.target.value)}>
              <option value="">Not in a category</option>
              {categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </Select>
          </div>
        </div>
        <div>
          <Label htmlFor="pkg-desc">Description</Label>
          <Textarea id="pkg-desc" value={description} onChange={(e) => setDescription(e.target.value)} rows={2} placeholder="What the client sees - what's included, fine print." />
        </div>

        <div className="space-y-3">
          <Label>What's in it</Label>
          {lines.map((l, idx) => (
            <div key={l.key} data-pkg-line className="space-y-2.5 rounded-xl border border-zinc-800 bg-zinc-900/30 p-3">
              <div className="flex items-center justify-between gap-2">
                <span className="text-xs font-semibold uppercase tracking-wide text-zinc-500">Line {idx + 1}</span>
                {lines.length > 1 && (
                  <button
                    type="button"
                    onClick={() => setLines((prev) => prev.filter((x) => x.key !== l.key))}
                    className="rounded p-1 text-zinc-500 hover:text-rose-300"
                    aria-label={`Remove line ${idx + 1}`}
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                )}
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label htmlFor={`pkg-n-${l.key}`} required>How many</Label>
                  <Input
                    id={`pkg-n-${l.key}`}
                    type="number"
                    min={1}
                    value={l.sessions}
                    onChange={(e) => setLine(l.key, { sessions: e.target.value })}
                  />
                </div>
                <div>
                  <Label htmlFor={`pkg-l-${l.key}`}>Called</Label>
                  <Input
                    id={`pkg-l-${l.key}`}
                    value={l.label}
                    onChange={(e) => setLine(l.key, { label: e.target.value })}
                    placeholder="e.g. IV drips"
                  />
                  <FieldHint>What the client sees in their wallet. Blank uses the treatment names.</FieldHint>
                </div>
              </div>

              {/* A line is treatments or a product, never both - "add the
                  ability to add products to packages and memberships". */}
              <div>
                <Label>This line is</Label>
                <div className="mt-1 grid gap-1.5 sm:grid-cols-2">
                  {([
                    { v: "service", label: "Treatments", blurb: "A visit they come in for" },
                    { v: "product", label: "A product", blurb: "Something off the shelf" },
                  ] as const).map((k) => (
                    <label
                      key={k.v}
                      className={cn(
                        "cursor-pointer rounded-lg border px-2.5 py-2 text-xs",
                        (k.v === "product") === !!l.productId
                          ? "border-fuchsia-500/60 bg-fuchsia-500/10"
                          : "border-zinc-800 hover:border-zinc-700",
                      )}
                    >
                      <input
                        type="radio"
                        className="sr-only"
                        name={`kind-${l.key}`}
                        checked={(k.v === "product") === !!l.productId}
                        onChange={() => setLine(l.key, k.v === "product"
                          ? { productId: products[0]?.id ?? "", serviceIds: [] }
                          : { productId: "" })}
                      />
                      <span className="block font-medium text-zinc-100">{k.label}</span>
                      <span className="mt-0.5 block text-[11px] leading-snug text-zinc-500">{k.blurb}</span>
                    </label>
                  ))}
                </div>
              </div>

              {l.productId ? (
                <div>
                  <Label htmlFor={`pkg-p-${l.key}`} required>Which product</Label>
                  <Select
                    id={`pkg-p-${l.key}`}
                    value={l.productId}
                    onChange={(e) => setLine(l.key, { productId: e.target.value })}
                  >
                    {products.length === 0 && <option value="">No products yet</option>}
                    {products.map((pr) => (
                      <option key={pr.id} value={pr.id}>{pr.name}</option>
                    ))}
                  </Select>
                  <FieldHint>
                    {products.length === 0
                      ? "Add it under Products first, then it can go in a package."
                      : `${l.sessions || 0} of these included, prepaid.`}
                  </FieldHint>
                </div>
              ) : (
                <div>
                  <Label>Which ones count</Label>
                  <div className="mt-1 max-h-44 overflow-y-auto rounded-lg border border-zinc-800">
                    {services.map((sv) => (
                      <label key={sv.id} className="flex cursor-pointer items-center gap-2 px-2.5 py-1.5 text-sm hover:bg-zinc-900/60">
                        <input
                          type="checkbox"
                          className={CHECKBOX}
                          checked={l.serviceIds.includes(sv.id)}
                          onChange={() => toggleService(l.key, sv.id)}
                        />
                        <span className="min-w-0 truncate text-zinc-200">{sv.name}</span>
                      </label>
                    ))}
                  </div>
                  <FieldHint>
                    {l.serviceIds.length === 0 ? "Tick the treatments this line pays for."
                      : l.serviceIds.length === 1 ? "One treatment."
                        : `${l.sessions || 0} to use across any of these ${l.serviceIds.length}.`}
                  </FieldHint>
                </div>
              )}

              <div>
                <Label>How they get it</Label>
                <div className="mt-1 grid gap-1.5 sm:grid-cols-3">
                  {FULFILMENTS.map((f) => (
                    <label
                      key={f.key}
                      className={cn(
                        "cursor-pointer rounded-lg border px-2.5 py-2 text-xs",
                        l.fulfilment === f.key ? "border-fuchsia-500/60 bg-fuchsia-500/10" : "border-zinc-800 hover:border-zinc-700",
                      )}
                    >
                      <input
                        type="radio"
                        className="sr-only"
                        name={`fulfil-${l.key}`}
                        checked={l.fulfilment === f.key}
                        onChange={() => setLine(l.key, { fulfilment: f.key })}
                      />
                      <span className="block font-medium text-zinc-100">{f.label}</span>
                      <span className="mt-0.5 block text-[11px] leading-snug text-zinc-500">{f.blurb}</span>
                    </label>
                  ))}
                </div>
              </div>
            </div>
          ))}
          <Button variant="secondary" size="sm" onClick={() => setLines((prev) => [...prev, newLine()])}>
            Add another line
          </Button>
        </div>

        <div>
          <Label htmlFor="pkg-price">Price for the whole package ($)</Label>
          <Input id="pkg-price" inputMode="decimal" value={price} onChange={(e) => setPrice(e.target.value)} placeholder="0.00" />
          <FieldHint>One price, however it reaches them.</FieldHint>
        </div>

        <ModalFooter>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button loading={saving} onClick={() => void save()}>{isNew ? "Create" : "Save"}</Button>
        </ModalFooter>
      </div>
    </Modal>
  );
}

/* ═══ Memberships tab ══════════════════════════════════════════════════════ */

/** "$50 credit + 1× Hydration Drip · 10% off" */
function perkSummary(m: Membership): string {
  const perks: string[] = [];
  if (m.credit_cents > 0) perks.push(`${formatMoney(m.credit_cents)} credit`);
  for (const b of m.benefits) {
    // A choice line has no single treatment to name, so use her wording, or
    // say how many it may be spent across.
    const what = b.label?.trim()
      || b.product_name
      || b.service_name
      || ((b.choice_names ?? []).length > 1 ? `any of ${(b.choice_names ?? []).length} treatments` : "included service");
    perks.push(`${b.sessions_per_cycle}× ${what}`);
  }
  const parts: string[] = [];
  if (perks.length > 0) parts.push(perks.join(" + "));
  if (m.discount_pct > 0) parts.push(`${m.discount_pct}% off`);
  return parts.length > 0 ? parts.join(" · ") : "No perks set";
}

function MembershipsTab() {
  const toast = useToast();
  const confirm = useConfirm();
  const [memberships, setMemberships] = useState<Membership[]>([]);
  const [services, setServices] = useState<Service[]>([]);
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState<Membership | "new" | null>(null);

  const load = useCallback(async () => {
    try {
      const [mems, svcs, prods] = await Promise.all([
        api.get<{ memberships: MembershipRow[] }>("/pos/memberships"),
        api.get<{ services: Service[] }>("/services"),
        api.get<{ products: Product[] }>("/payments/products"),
      ]);
      setMemberships(mems.memberships.map(toMembership));
      setServices(svcs.services.filter((s) => !s.archived));
      setProducts(prods.products.filter((x) => !x.archived && x.pricing_type !== "subscription"));
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => { void load(); }, [load]);

  const archive = async (m: Membership) => {
    const ok = await confirm({
      title: `Archive ${m.name}?`,
      description: m.active_members > 0
        ? `${m.active_members} ${m.active_members === 1 ? "member is" : "members are"} on this plan — they keep their benefits, but nobody new can join.`
        : "It can't be sold anymore. Existing members keep their benefits.",
      confirmLabel: "Archive",
      danger: true,
    });
    if (!ok) return;
    try {
      await api.patch(`/pos/memberships/${m.id}`, { archived: true });
      toast.success("Membership archived");
      void load();
    } catch (err) {
      toast.error(errMsg(err));
    }
  };

  if (loading) {
    return <div className="space-y-2">{Array.from({ length: 3 }).map((_, i) => <Skeleton key={i} className="h-16" />)}</div>;
  }

  return (
    <div className="space-y-3">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
        <p className="max-w-2xl text-sm text-zinc-500">
          Sold with a payment link, a membership renews on the client&apos;s card every cycle. Their credit and included visits arrive each time a payment goes through.
        </p>
        <Button size="sm" className="shrink-0" leftIcon={<Plus className="h-3.5 w-3.5" />} onClick={() => setEditing("new")}>
          New membership
        </Button>
      </div>

      {memberships.length === 0 ? (
        <EmptyState
          icon={Crown}
          title="No memberships yet"
          description="Turn regulars into members — a flat fee each cycle buys them account credit, included visits, and a standing discount."
          action={<Button size="sm" leftIcon={<Plus className="h-3.5 w-3.5" />} onClick={() => setEditing("new")}>New membership</Button>}
        />
      ) : (
        <div className="overflow-hidden rounded-xl border border-zinc-800/70 bg-zinc-900/30">
          {memberships.map((m, i) => (
            <div
              key={m.id}
              className={cn("group flex items-center gap-3 px-4 py-3 hover:bg-zinc-900/40", i > 0 && "border-t border-zinc-800/40")}
            >
              <Crown className="h-4 w-4 shrink-0 text-zinc-500" />
              <div className="min-w-0 flex-1">
                <div className="truncate text-sm font-medium text-zinc-100">{m.name}</div>
                <p className="mt-0.5 truncate text-xs text-zinc-500">
                  {perkSummary(m)}
                  <span className="mx-1.5 text-zinc-700">·</span>
                  {m.active_members} active {m.active_members === 1 ? "member" : "members"}
                </p>
              </div>
              <div className="shrink-0 text-right text-sm font-medium text-zinc-200 tabular-nums">
                {formatMoney(m.price_cents)}
                <span className="text-xs font-normal text-zinc-500">{perCycle(m.interval)}</span>
              </div>
              <div className="flex shrink-0 items-center gap-1 opacity-0 transition-opacity group-hover:opacity-100 focus-within:opacity-100">
                <button
                  type="button"
                  onClick={() => setEditing(m)}
                  className="inline-flex h-7 w-7 items-center justify-center rounded text-zinc-400 hover:bg-zinc-800 hover:text-zinc-200"
                  aria-label={`Edit ${m.name}`}
                >
                  <Pencil className="h-3.5 w-3.5" />
                </button>
                <button
                  type="button"
                  onClick={() => void archive(m)}
                  className="inline-flex h-7 w-7 items-center justify-center rounded text-zinc-400 hover:bg-zinc-800 hover:text-rose-300"
                  aria-label={`Archive ${m.name}`}
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {editing !== null && (
        <MembershipDrawer
          membership={editing}
          services={services}
          products={products}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); void load(); }}
        />
      )}
    </div>
  );
}

/**
 * One included line on a membership.
 *
 * "Yes memberships need to be created like packages" - so this is the same
 * shape as a package line: a set of treatments it may be spent on, a count,
 * and her own wording for the wallet.
 */
interface BenefitDraft {
  key: string;
  serviceIds: string[];
  /** A product included with the plan instead of a treatment. */
  productId: string;
  sessions: string;
  label: string;
}

function MembershipDrawer({
  membership, services, products, onClose, onSaved,
}: {
  membership: Membership | "new";
  services: Service[];
  products: Product[];
  onClose: () => void;
  onSaved: () => void;
}) {
  const toast = useToast();
  const isNew = membership === "new";
  const base = isNew ? null : membership;

  const [name, setName] = useState(base?.name ?? "");
  const [description, setDescription] = useState(base?.description ?? "");
  const [price, setPrice] = useState(base ? centsToDollars(base.price_cents) : "");
  const [interval, setInterval] = useState<Interval>(base?.interval ?? "month");
  const [credit, setCredit] = useState(base ? centsToDollars(base.credit_cents) : "");
  const [discountPct, setDiscountPct] = useState(base ? String(base.discount_pct) : "0");
  const [benefits, setBenefits] = useState<BenefitDraft[]>(
    () => (base?.benefits ?? []).map((b, i) => ({
      key: `b${i}`,
      // A line written before choice lines existed has one treatment in
      // service_id; a newer one lists them all in service_ids.
      serviceIds: (b.service_ids ?? []).length > 0
        ? [...(b.service_ids ?? [])]
        : b.service_id ? [b.service_id] : [],
      productId: b.product_id ?? "",
      sessions: String(b.sessions_per_cycle),
      label: b.label ?? "",
    }))
  );
  const [saving, setSaving] = useState(false);

  const setBenefit = (idx: number, patch: Partial<BenefitDraft>) => {
    setBenefits((prev) => prev.map((b, i) => (i === idx ? { ...b, ...patch } : b)));
  };
  const toggleBenefitService = (idx: number, id: string) => {
    setBenefits((prev) => prev.map((b, i) => i !== idx ? b : {
      ...b,
      serviceIds: b.serviceIds.includes(id) ? b.serviceIds.filter((x) => x !== id) : [...b.serviceIds, id],
    }));
  };
  const addBenefit = () => {
    setBenefits((prev) => [...prev, {
      key: `b${Date.now()}`, serviceIds: [], productId: "", sessions: "1", label: "",
    }]);
  };
  const removeBenefit = (idx: number) => {
    setBenefits((prev) => prev.filter((_, i) => i !== idx));
  };

  const save = async () => {
    if (!name.trim()) { toast.error("Give the membership a name"); return; }
    const pct = discountPct.trim() === "" ? 0 : parseFloat(discountPct);
    if (!Number.isFinite(pct) || pct < 0 || pct > 100) { toast.error("Member discount must be between 0 and 100"); return; }
    const cleanedBenefits: {
      service_id: string | null; service_ids: string[]; product_id: string | null;
      sessions_per_cycle: number; label: string | null;
    }[] = [];
    for (const b of benefits) {
      if (!b.productId && b.serviceIds.length === 0) {
        toast.error("Tick a treatment on every included line, or pick a product"); return;
      }
      const sessions = parseInt(b.sessions, 10);
      if (!Number.isFinite(sessions) || sessions < 1 || sessions > 50) {
        toast.error("Included visits must be between 1 and 50 per cycle"); return;
      }
      cleanedBenefits.push({
        service_ids: b.productId ? [] : b.serviceIds,
        // Kept in step for anything still reading the single-treatment field.
        service_id: !b.productId && b.serviceIds.length === 1 ? b.serviceIds[0]! : null,
        product_id: b.productId || null,
        sessions_per_cycle: sessions,
        label: b.label.trim() || null,
      });
    }
    const body = {
      name: name.trim(),
      description: description.trim() || null,
      price_cents: dollarsToCents(price),
      interval,
      credit_cents: dollarsToCents(credit),
      discount_pct: pct,
      benefits: cleanedBenefits,
    };
    setSaving(true);
    try {
      if (isNew) {
        await api.post("/pos/memberships", body);
        toast.success("Membership created");
      } else {
        await api.patch(`/pos/memberships/${base!.id}`, body);
        toast.success("Membership updated");
      }
      onSaved();
    } catch (err) {
      toast.error(errMsg(err));
      setSaving(false);
    }
  };

  return (
    <Drawer open onClose={onClose} title={isNew ? "New membership" : `Edit ${base?.name}`} width="lg">
      <div className="space-y-4 p-5">
        <div>
          <Label htmlFor="mem-name" required>Name</Label>
          <Input id="mem-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Glow Club" autoFocus />
        </div>

        <div>
          <Label htmlFor="mem-desc">Description</Label>
          <Textarea
            id="mem-desc"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={2}
            placeholder="Optional — what members get, fine print."
          />
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label htmlFor="mem-price">Price ($)</Label>
            <Input id="mem-price" inputMode="decimal" value={price} onChange={(e) => setPrice(e.target.value)} placeholder="0.00" />
            {!isNew && (base?.active_members ?? 0) > 0 && (
              <FieldHint>A new price applies to new members. Current members keep the price they signed up at.</FieldHint>
            )}
          </div>
          <div>
            <Label htmlFor="mem-interval">Interval</Label>
            <Select
              id="mem-interval"
              value={interval}
              onChange={(e) => setInterval(e.target.value === "year" ? "year" : e.target.value === "two_months" ? "two_months" : "month")}
            >
              <option value="month">Monthly</option>
              <option value="two_months">Every 2 months</option>
              <option value="year">Yearly</option>
            </Select>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label htmlFor="mem-credit">Credit each cycle ($)</Label>
            <Input id="mem-credit" inputMode="decimal" value={credit} onChange={(e) => setCredit(e.target.value)} placeholder="0.00" />
            <FieldHint>Lands on their account each cycle to spend on anything.</FieldHint>
          </div>
          <div>
            <Label htmlFor="mem-discount">Member discount (%)</Label>
            <Input
              id="mem-discount"
              type="number"
              min={0}
              max={100}
              step={1}
              value={discountPct}
              onChange={(e) => setDiscountPct(e.target.value)}
            />
          </div>
        </div>

        <div className="rounded-lg border border-zinc-800 p-3">
          <h3 className={cn(SECTION_HEADING, "mb-2")}>Included visits each cycle</h3>
          {services.length === 0 ? (
            <p className="text-xs text-zinc-500">Build your service menu first — an included visit points at a service.</p>
          ) : (
            <div className="space-y-2">
              {benefits.length === 0 && (
                <p className="text-xs text-zinc-500">None — this membership is credit and discount only.</p>
              )}
              {/* Laid out exactly like a package line, because she asked for
                  memberships to be "created like packages" and already knows
                  that screen: how many, what it is called, which treatments
                  it may be spent on. */}
              {benefits.map((b, i) => (
                <div key={b.key} className="space-y-2 rounded-lg border border-zinc-800 bg-zinc-900/40 p-2.5">
                  <div className="flex items-start gap-2">
                    <div className="w-24 shrink-0">
                      <Label htmlFor={`mb-n-${b.key}`} required>How many</Label>
                      <Input
                        id={`mb-n-${b.key}`}
                        type="number"
                        min={1}
                        max={50}
                        value={b.sessions}
                        onChange={(e) => setBenefit(i, { sessions: e.target.value })}
                        className="h-8 text-xs"
                      />
                    </div>
                    <div className="min-w-0 flex-1">
                      <Label htmlFor={`mb-l-${b.key}`}>Called</Label>
                      <Input
                        id={`mb-l-${b.key}`}
                        value={b.label}
                        onChange={(e) => setBenefit(i, { label: e.target.value })}
                        className="h-8 text-xs"
                        placeholder="e.g. IV drips"
                      />
                    </div>
                    <button
                      type="button"
                      onClick={() => removeBenefit(i)}
                      className="mt-5 inline-flex h-7 w-7 shrink-0 items-center justify-center rounded text-zinc-500 hover:bg-zinc-800 hover:text-rose-300"
                      aria-label="Remove this included line"
                    >
                      <X className="h-3.5 w-3.5" />
                    </button>
                  </div>
                  {/* Treatments or a product, the same choice a package
                      line offers. */}
                  <div className="flex gap-1.5">
                    {([
                      { v: "service", label: "Treatments" },
                      { v: "product", label: "A product" },
                    ] as const).map((k) => (
                      <button
                        key={k.v}
                        type="button"
                        onClick={() => setBenefit(i, k.v === "product"
                          ? { productId: products[0]?.id ?? "", serviceIds: [] }
                          : { productId: "" })}
                        className={cn(
                          "rounded-full px-2.5 py-0.5 text-[11px] font-medium ring-1 ring-inset",
                          (k.v === "product") === !!b.productId
                            ? "bg-fuchsia-500/15 text-fuchsia-300 ring-fuchsia-500/30"
                            : "bg-zinc-800/70 text-zinc-400 ring-zinc-700 hover:text-zinc-200",
                        )}
                      >
                        {k.label}
                      </button>
                    ))}
                  </div>
                  {b.productId ? (
                    <div>
                      <Label htmlFor={`mb-p-${b.key}`} required>Which product</Label>
                      <Select
                        id={`mb-p-${b.key}`}
                        value={b.productId}
                        onChange={(e) => setBenefit(i, { productId: e.target.value })}
                        className="h-8 text-xs"
                      >
                        {products.length === 0 && <option value="">No products yet</option>}
                        {products.map((pr) => (
                          <option key={pr.id} value={pr.id}>{pr.name}</option>
                        ))}
                      </Select>
                      <FieldHint>
                        {products.length === 0
                          ? "Add it under Products first, then it can be part of a plan."
                          : `${b.sessions || 0} a cycle, prepaid with the membership.`}
                      </FieldHint>
                    </div>
                  ) : (
                    <div>
                      <Label>Which ones count</Label>
                      <div className="mt-1 max-h-40 overflow-y-auto rounded-lg border border-zinc-800">
                        {services.map((sv) => (
                          <label key={sv.id} className="flex cursor-pointer items-center gap-2 px-2.5 py-1.5 text-xs hover:bg-zinc-900/60">
                            <input
                              type="checkbox"
                              className={CHECKBOX}
                              checked={b.serviceIds.includes(sv.id)}
                              onChange={() => toggleBenefitService(i, sv.id)}
                            />
                            <span className="min-w-0 truncate text-zinc-200">{sv.name}</span>
                          </label>
                        ))}
                      </div>
                      <FieldHint>
                        {b.serviceIds.length === 0 ? "Tick the treatments this line pays for."
                          : b.serviceIds.length === 1 ? "One treatment, every cycle."
                            : `${b.sessions || 0} a cycle, to use across any of these ${b.serviceIds.length}.`}
                      </FieldHint>
                    </div>
                  )}
                </div>
              ))}
              <button type="button" onClick={addBenefit} className="text-xs text-indigo-400 hover:text-indigo-300">
                + Add an included line
              </button>
            </div>
          )}
        </div>

        <div className="flex items-center justify-end gap-2 border-t border-zinc-800 pt-4">
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button loading={saving} onClick={() => void save()}>{isNew ? "Create membership" : "Save changes"}</Button>
        </div>
      </div>
    </Drawer>
  );
}


/* ═══ Products tab ═════════════════════════════════════════════════════════ */
// "We also have products to sell, can we do them online? And can the software
// keep a count of them?" Retail products live here: the price the counter
// charges, whether it's on the web shop, and a running count that checkout,
// the shop and refunds all move. Every change to a count is written down.

interface ShopConfig { shipping_cents: number | null; shop_url: string | null }
interface StockMove {
  id: string; change: number; quantity_after: number | null; reason: string; note: string | null;
  created_at: string; invoice_number: string | null; by_name: string | null;
}

const STOCK_REASON: Record<string, string> = {
  sale: "Sold", refund: "Returned", received: "Delivery", count: "Counted", practice_undo: "Practice undone",
};

function ProductsTab() {
  const toast = useToast();
  const confirm = useConfirm();
  const [products, setProducts] = useState<Product[] | null>(null);
  const [shop, setShop] = useState<ShopConfig | null>(null);
  const [editing, setEditing] = useState<Product | "new" | null>(null);
  const [stockFor, setStockFor] = useState<Product | null>(null);

  const load = useCallback(async () => {
    try {
      const [p, sh] = await Promise.all([
        api.get<{ products: Product[] }>("/payments/products"),
        api.get<ShopConfig>("/payments/shop"),
      ]);
      setProducts(p.products.filter((x) => x.pricing_type !== "subscription"));
      setShop(sh);
    } catch (err) {
      toast.error(errMsg(err));
      setProducts((prev) => prev ?? []);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => { void load(); }, [load]);

  const archive = async (p: Product) => {
    const ok = await confirm({
      title: `Remove ${p.name}?`,
      description: "It comes off checkout and the web shop. Past sales keep it.",
      confirmLabel: "Remove",
      danger: true,
    });
    if (!ok) return;
    try {
      await api.delete(`/payments/products/${p.id}`);
      toast.success("Product removed");
      void load();
    } catch (err) { toast.error(errMsg(err)); }
  };

  if (!products) return <div className="space-y-2">{Array.from({ length: 3 }).map((_, i) => <Skeleton key={i} className="h-16" />)}</div>;

  return (
    <div className="space-y-4">
      {shop && <ShopCard shop={shop} onSaved={(next) => setShop({ ...shop, ...next })} />}

      <div className="flex items-center justify-between gap-2">
        <p className="text-xs text-zinc-500">
          Sold at checkout, and on your web shop when &ldquo;Sell online&rdquo; is on. Counts go down when a sale is paid and back up on a refund you mark as returned.
        </p>
        <Button size="sm" leftIcon={<Plus className="h-3.5 w-3.5" />} onClick={() => setEditing("new")}>New product</Button>
      </div>

      {products.length === 0 ? (
        <EmptyState icon={ShoppingBag} title="No products yet" description="Add the retail products you sell, with a price and how many you have." />
      ) : (
        <div className="overflow-hidden rounded-xl border border-zinc-800/70 bg-zinc-900/30">
          {products.map((p, i) => {
            const low = p.track_inventory && p.low_stock_at != null && (p.in_stock ?? 0) <= p.low_stock_at;
            return (
              <div key={p.id} className={cn("group flex items-center gap-3 px-4 py-3 hover:bg-zinc-900/40", i > 0 && "border-t border-zinc-800/40")}>
                {p.image_url
                  ? <img src={p.image_url} alt="" className="h-10 w-10 shrink-0 rounded-lg object-cover ring-1 ring-zinc-800" />
                  : <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-zinc-800/60"><ShoppingBag className="h-4 w-4 text-zinc-500" /></div>}
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="truncate text-sm font-medium text-zinc-100">{p.name}</span>
                    {p.sell_online && <Badge color="emerald" size="xs">Sold online</Badge>}
                  </div>
                  <p className="mt-0.5 text-xs text-zinc-500">
                    {p.track_inventory
                      ? <span className={cn(low && "text-amber-300")}>{p.in_stock ?? 0} in stock{low ? ", time to reorder" : ""}</span>
                      : "Not counting stock"}
                    {p.sku ? <span className="text-zinc-600"> · SKU {p.sku}</span> : null}
                  </p>
                </div>
                <span className="shrink-0 text-sm font-medium tabular-nums text-zinc-200">{formatMoney(p.amount_cents)}</span>
                <div className="flex shrink-0 items-center gap-1">
                  {p.track_inventory && (
                    <Button size="sm" variant="secondary" onClick={() => setStockFor(p)}>Stock</Button>
                  )}
                  <button type="button" onClick={() => setEditing(p)} aria-label={`Edit ${p.name}`}
                    className="inline-flex h-7 w-7 items-center justify-center rounded text-zinc-400 hover:bg-zinc-800 hover:text-zinc-200">
                    <Pencil className="h-3.5 w-3.5" />
                  </button>
                  <button type="button" onClick={() => void archive(p)} aria-label={`Remove ${p.name}`}
                    className="inline-flex h-7 w-7 items-center justify-center rounded text-zinc-400 hover:bg-zinc-800 hover:text-rose-300">
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {editing !== null && (
        <ProductDrawer
          product={editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); void load(); }}
        />
      )}
      {stockFor && (
        <StockModal
          product={stockFor}
          onClose={() => setStockFor(null)}
          onChanged={() => void load()}
        />
      )}
    </div>
  );
}

/** The shop's address to share, and whether it offers shipping. */
function ShopCard({ shop, onSaved }: { shop: ShopConfig; onSaved: (s: Partial<ShopConfig>) => void }) {
  const toast = useToast();
  const [shipOn, setShipOn] = useState(shop.shipping_cents !== null);
  const [ship, setShip] = useState(shop.shipping_cents !== null ? centsToDollars(shop.shipping_cents) : "");
  const [saving, setSaving] = useState(false);
  const url = shop.shop_url ?? `${window.location.origin}/shop`;

  const save = async () => {
    setSaving(true);
    try {
      const shipping_cents = shipOn ? dollarsToCents(ship || "0") : null;
      await api.put("/payments/shop", { shipping_cents });
      onSaved({ shipping_cents });
      toast.success("Shop settings saved");
    } catch (err) { toast.error(errMsg(err)); }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-3 rounded-xl border border-zinc-800/70 bg-zinc-900/30 px-4 py-3">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <div className="min-w-0">
          <div className="flex items-center gap-2 text-sm text-zinc-200">
            <ShoppingBag className="h-4 w-4 shrink-0 text-indigo-400" />
            <span className="truncate font-mono text-xs sm:text-sm">{url}</span>
          </div>
          <p className="mt-1 text-xs text-zinc-500">Your web shop. Link it from your website; it lists the products with &ldquo;Sell online&rdquo; on.</p>
        </div>
        <Button variant="secondary" size="sm" leftIcon={<Copy className="h-3.5 w-3.5" />}
          onClick={() => { void navigator.clipboard.writeText(url).then(() => toast.success("Shop link copied")).catch(() => toast.error("Couldn't copy")); }}>
          Copy
        </Button>
      </div>
      <div className="flex flex-wrap items-center gap-3 border-t border-zinc-800/60 pt-3">
        <span className="text-xs text-zinc-400">Pick up at the clinic is always offered.</span>
        <label className="flex cursor-pointer items-center gap-2 text-sm text-zinc-300">
          <input type="checkbox" className={CHECKBOX} checked={shipOn} onChange={(e) => setShipOn(e.target.checked)} />
          Also ship for
        </label>
        <Input className="h-8 w-24" inputMode="decimal" value={ship} disabled={!shipOn}
          onChange={(e) => setShip(e.target.value)} placeholder="8.00" />
        <Button size="sm" variant="secondary" loading={saving} onClick={() => void save()}>Save</Button>
      </div>
    </div>
  );
}

function ProductDrawer({ product, onClose, onSaved }: { product: Product | "new"; onClose: () => void; onSaved: () => void }) {
  const toast = useToast();
  const isNew = product === "new";
  const base = isNew ? null : product;
  const [name, setName] = useState(base?.name ?? "");
  const [price, setPrice] = useState(base ? centsToDollars(base.amount_cents) : "");
  const [description, setDescription] = useState(base?.description ?? "");
  const [imageUrl, setImageUrl] = useState(base?.image_url ?? "");
  const [sellOnline, setSellOnline] = useState(base?.sell_online ?? false);
  const [track, setTrack] = useState(base?.track_inventory ?? false);
  const [inStock, setInStock] = useState(base?.track_inventory ? String(base.in_stock ?? 0) : "");
  const [lowAt, setLowAt] = useState(base?.low_stock_at != null ? String(base.low_stock_at) : "");
  const [sku, setSku] = useState(base?.sku ?? "");
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);

  const onPhoto = async (file: File | undefined) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) { toast.error("Choose a photo (JPG or PNG)"); return; }
    setUploading(true);
    try {
      const up = await uploadFile(file, { path: `shop/products/${makeUploadFilename(file.name, "product")}`, bucket: "public" });
      setImageUrl(up.publicUrl);
    } catch (err) { toast.error(errMsg(err)); }
    finally { setUploading(false); }
  };

  const save = async () => {
    if (!name.trim()) { toast.error("Give the product a name"); return; }
    const count = inStock.trim() === "" ? undefined : parseInt(inStock, 10);
    if (track && count !== undefined && !Number.isFinite(count)) { toast.error("Stock must be a whole number"); return; }
    const body: Record<string, unknown> = {
      name: name.trim(),
      amount_cents: dollarsToCents(price),
      description: description.trim() || null,
      image_url: imageUrl.trim() || null,
      sell_online: sellOnline,
      track_inventory: track,
      low_stock_at: lowAt.trim() === "" ? null : Math.max(0, parseInt(lowAt, 10) || 0),
      sku: sku.trim() || null,
    };
    // The count only goes along when it changed, so a sale made while this
    // drawer was open isn't overwritten.
    const countChanged = track && count !== undefined && (!base?.track_inventory || count !== (base.in_stock ?? 0));
    if (countChanged) body.in_stock = count;
    setSaving(true);
    try {
      if (isNew) await api.post("/payments/products", body);
      else await api.patch(`/payments/products/${base!.id}`, body);
      toast.success(isNew ? "Product added" : "Product saved");
      onSaved();
    } catch (err) {
      toast.error(errMsg(err));
      setSaving(false);
    }
  };

  return (
    <Drawer open onClose={onClose} title={isNew ? "New product" : `Edit ${base?.name}`} width="lg">
      <div className="space-y-4 p-5">
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-[1fr_8rem]">
          <div>
            <Label htmlFor="prod-name" required>Name</Label>
            <Input id="prod-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. D3/K2" autoFocus />
          </div>
          <div>
            <Label htmlFor="prod-price">Price ($)</Label>
            <Input id="prod-price" inputMode="decimal" value={price} onChange={(e) => setPrice(e.target.value)} placeholder="0.00" />
          </div>
        </div>
        <div>
          <Label htmlFor="prod-desc">Description</Label>
          <Textarea id="prod-desc" value={description} onChange={(e) => setDescription(e.target.value)} rows={3}
            placeholder="What clients see on the web shop." />
        </div>
        <div>
          <Label>Photo</Label>
          <div className="flex items-center gap-3">
            {imageUrl
              ? <img src={imageUrl} alt="" className="h-16 w-16 rounded-lg object-cover ring-1 ring-zinc-800" />
              : <div className="flex h-16 w-16 items-center justify-center rounded-lg bg-zinc-800/60"><ShoppingBag className="h-5 w-5 text-zinc-500" /></div>}
            <label className="inline-flex cursor-pointer items-center rounded-lg border border-zinc-800 px-3 py-1.5 text-xs text-zinc-200 hover:border-zinc-600">
              {uploading ? "Uploading…" : imageUrl ? "Change photo" : "Add a photo"}
              <input type="file" accept="image/*" className="hidden" onChange={(e) => void onPhoto(e.target.files?.[0])} />
            </label>
            {imageUrl && (
              <button type="button" className="text-xs text-zinc-500 hover:text-rose-300" onClick={() => setImageUrl("")}>Remove</button>
            )}
          </div>
        </div>
        <label className="flex cursor-pointer items-start gap-2 text-sm text-zinc-300">
          <input type="checkbox" className={cn(CHECKBOX, "mt-0.5")} checked={sellOnline} onChange={(e) => setSellOnline(e.target.checked)} />
          <span>
            Sell online
            <span className="block text-xs text-zinc-500">Shows on your web shop. Clients pay by card and pick up (or you ship it).</span>
          </span>
        </label>
        <div className="rounded-lg border border-zinc-800 p-3">
          <label className="flex cursor-pointer items-start gap-2 text-sm text-zinc-300">
            <input type="checkbox" className={cn(CHECKBOX, "mt-0.5")} checked={track} onChange={(e) => setTrack(e.target.checked)} />
            <span>
              Keep a count
              <span className="block text-xs text-zinc-500">Every paid sale takes one off. The web shop won&rsquo;t sell more than you have.</span>
            </span>
          </label>
          {track && (
            <div className="mt-3 grid grid-cols-2 gap-3 sm:grid-cols-3">
              <div>
                <Label htmlFor="prod-stock">In stock now</Label>
                <Input id="prod-stock" inputMode="numeric" value={inStock} onChange={(e) => setInStock(e.target.value)} placeholder="0" />
              </div>
              <div>
                <Label htmlFor="prod-low">Remind me at</Label>
                <Input id="prod-low" inputMode="numeric" value={lowAt} onChange={(e) => setLowAt(e.target.value)} placeholder="e.g. 3" />
                <FieldHint>Leaves a reorder task.</FieldHint>
              </div>
              <div>
                <Label htmlFor="prod-sku">SKU (optional)</Label>
                <Input id="prod-sku" value={sku} onChange={(e) => setSku(e.target.value)} />
              </div>
            </div>
          )}
        </div>
        <div className="flex items-center justify-end gap-2 border-t border-zinc-800 pt-4">
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button loading={saving} disabled={uploading} onClick={() => void save()}>{isNew ? "Add product" : "Save changes"}</Button>
        </div>
      </div>
    </Drawer>
  );
}

/** A delivery in, or a shelf count, plus the history of every change. */
function StockModal({ product, onClose, onChanged }: { product: Product; onClose: () => void; onChanged: () => void }) {
  const toast = useToast();
  const [kind, setKind] = useState<"received" | "count">("received");
  const [qty, setQty] = useState("");
  const [note, setNote] = useState("");
  const [moves, setMoves] = useState<StockMove[] | null>(null);
  const [current, setCurrent] = useState<number>(product.in_stock ?? 0);
  const [saving, setSaving] = useState(false);

  const loadMoves = useCallback(() => {
    api.get<{ movements: StockMove[] }>(`/payments/products/${product.id}/stock`)
      .then((d) => setMoves(d.movements)).catch(() => setMoves([]));
  }, [product.id]);
  useEffect(() => { loadMoves(); }, [loadMoves]);

  const save = async () => {
    const n = parseInt(qty, 10);
    if (!Number.isFinite(n) || (kind === "received" && n <= 0) || (kind === "count" && n < 0)) {
      toast.error(kind === "received" ? "How many arrived?" : "How many are on the shelf?");
      return;
    }
    setSaving(true);
    try {
      const d = await api.post<{ in_stock: number | null }>(`/payments/products/${product.id}/stock`, { kind, qty: n, note: note.trim() || undefined });
      setCurrent(d.in_stock ?? current);
      setQty(""); setNote("");
      toast.success(`${product.name}: ${d.in_stock ?? current} in stock`);
      loadMoves();
      onChanged();
    } catch (err) { toast.error(errMsg(err)); }
    finally { setSaving(false); }
  };

  return (
    <Modal open onClose={onClose} title={`${product.name} stock`} size="md">
      <div className="space-y-4">
        <p className="text-sm text-zinc-300"><span className="text-2xl font-semibold tabular-nums text-zinc-100">{current}</span> in stock now</p>
        <div className="flex gap-1 rounded-lg border border-zinc-800 p-1 text-xs">
          {(["received", "count"] as const).map((k) => (
            <button key={k} type="button" onClick={() => setKind(k)}
              className={cn("flex-1 rounded-md px-2 py-1.5", kind === k ? "bg-indigo-500/15 text-indigo-200" : "text-zinc-400 hover:text-zinc-200")}>
              {k === "received" ? "A delivery arrived" : "I counted the shelf"}
            </button>
          ))}
        </div>
        <div className="grid grid-cols-[8rem_1fr] gap-3">
          <div>
            <Label>{kind === "received" ? "How many arrived" : "How many are there"}</Label>
            <Input inputMode="numeric" value={qty} onChange={(e) => setQty(e.target.value)} autoFocus />
          </div>
          <div>
            <Label>Note (optional)</Label>
            <Input value={note} onChange={(e) => setNote(e.target.value)} placeholder={kind === "received" ? "e.g. invoice number" : "e.g. monthly count"} />
          </div>
        </div>
        <div className="flex justify-end">
          <Button loading={saving} onClick={() => void save()}>{kind === "received" ? "Add to stock" : "Set the count"}</Button>
        </div>
        <div>
          <h4 className={cn(SECTION_HEADING, "mb-1")}>History</h4>
          {moves === null ? <Skeleton className="h-20" /> : moves.length === 0 ? (
            <p className="text-xs text-zinc-500">No changes yet.</p>
          ) : (
            <ul className="max-h-60 divide-y divide-zinc-800/50 overflow-y-auto text-xs">
              {moves.map((m) => (
                <li key={m.id} className="flex items-center justify-between gap-2 py-1.5">
                  <span className="min-w-0 text-zinc-300">
                    {STOCK_REASON[m.reason] ?? m.reason}
                    {m.invoice_number ? <span className="text-zinc-500"> {m.invoice_number}</span> : null}
                    {m.note ? <span className="text-zinc-500"> · {m.note}</span> : null}
                    <span className="block text-zinc-600">{formatDateTime(m.created_at)}{m.by_name ? ` · ${m.by_name}` : ""}</span>
                  </span>
                  <span className={cn("shrink-0 tabular-nums", m.change > 0 ? "text-emerald-300" : "text-zinc-400")}>
                    {m.change > 0 ? `+${m.change}` : m.change}
                    {m.quantity_after != null && <span className="text-zinc-600"> → {m.quantity_after}</span>}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
      <ModalFooter>
        <Button variant="ghost" onClick={onClose}>Done</Button>
      </ModalFooter>
    </Modal>
  );
}
